import React, { useState, useEffect } from "react";
import axios from "axios";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PersonIcon from '@mui/icons-material/Person';
import { useLocation } from "react-router-dom";

const AdminTickets = ({ defaultTab }) => {
  const location = useLocation();
  // --- Tab State --- prop takes priority (refresh-safe), then nav state, then default "inquiry"
  const [activeTab, setActiveTab] = useState(defaultTab || location.state?.tab || "inquiry"); // "inquiry" | "order"

  useEffect(() => {
    if (defaultTab) setActiveTab(defaultTab);
  }, [defaultTab]);

  // Clicking a notification (see notificationsPage.js) navigates here with
  // location.state.autoOpenTicketNumber set to the specific ticket it was
  // about — this opens that ticket's details modal once its list has
  // loaded. Consumed once so it doesn't reopen if the user closes the
  // modal and something else re-renders this component.
  const autoOpenConsumedRef = React.useRef(false);

  // --- Inquiry Tickets State ---
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [closingInquiryTicket, setClosingInquiryTicket] = useState(false);
  const [reopeningInquiryTicket, setReopeningInquiryTicket] = useState(false);
  const [userProfilePic, setUserProfilePic] = useState(null);
  useEffect(() => {
    const cached = localStorage.getItem("profileImageData");
    if (cached) setUserProfilePic(cached);
  }, []);

  // --- Order Tickets State ---
  const [orderTickets, setOrderTickets] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [orderCurrentPage, setOrderCurrentPage] = useState(1);
  const [orderItemsPerPage, setOrderItemsPerPage] = useState(50);
  const [orderFilterStatus, setOrderFilterStatus] = useState("all");
  const [orderFilterPriority, setOrderFilterPriority] = useState("all");
  const [orderFilterCategory, setOrderFilterCategory] = useState("all");
  const [orderSearchText, setOrderSearchText] = useState("");
  const [selectedOrderTicket, setSelectedOrderTicket] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [closingOrderTicket, setClosingOrderTicket] = useState(false);
  const [reopeningOrderTicket, setReopeningOrderTicket] = useState(false);

  // --- Shared / Inquiry States ---
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // --- Assigned To (UI-only mock, no backend yet — API pending) ---
  const [ticketAssignedAdminSelection, setTicketAssignedAdminSelection] = useState('');
  // TODO: replace with a real fetch of Admin/Super Admin/Inquiry Admin users once the API is ready.
  const DUMMY_ADMIN_LIST_TICKETS = [
    { id: 1, name: 'Omneet Choudhary', role: 'SUPER_ADMIN' },
    { id: 2, name: 'Swapnil Yadav', role: 'ADMIN' },
    { id: 3, name: 'Hardik Paija', role: 'INQUIRY_ADMIN' },
    { id: 4, name: 'Harry Potter', role: 'INQUIRY_ADMIN' },
  ];
  const currentAdminRoleTickets = (typeof window !== 'undefined' && localStorage.getItem('adminRole')) || '';
  const canManageTicketAssignment = currentAdminRoleTickets === 'SUPER_ADMIN' || currentAdminRoleTickets === 'ADMIN';

  useEffect(() => {
    if (selectedTicket) {
      setTicketAssignedAdminSelection(
        selectedTicket.assignedAdminId ? String(selectedTicket.assignedAdminId) : ''
      );
    }
  }, [selectedTicket]);

  const handleTicketAssignedAdminChange = (e) => {
    const newId = e.target.value;
    setTicketAssignedAdminSelection(newId);
    // TODO: wire up the save-assignment API call here once it's available.
    // e.g. PUT /admin-dashboard-panel/assign-ticket/{ticketId} with { assignedAdminId: newId }
  };
  const [showDetails, setShowDetails] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [updatedStatus, setUpdatedStatus] = useState("");
  const [updatedAssignee, setUpdatedAssignee] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterType, setFilterType] = useState("all");

  useEffect(() => {
    const targetTicketNumber = location.state?.autoOpenTicketNumber;
    if (!targetTicketNumber || autoOpenConsumedRef.current) return;

    const isOrderTicket = targetTicketNumber.toUpperCase().startsWith("ORT");
    const list = isOrderTicket ? orderTickets : tickets;
    if (list.length === 0) return; // list hasn't loaded yet — try again once it does

    const match = list.find((t) => t.ticketNumber === targetTicketNumber);
    if (!match) return; // loaded but not found (e.g. deleted) — nothing to open

    autoOpenConsumedRef.current = true;
    if (isOrderTicket) {
      setSelectedOrderTicket(match);
      setShowOrderDetails(true);
    } else {
      setSelectedTicket(match);
      setUpdatedStatus(match.status);
      setUpdatedAssignee(match.assignedTo);
      setShowDetails(true);
    }
  }, [location.state, tickets, orderTickets]);
  const [searchText, setSearchText] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showReplyChat, setShowReplyChat] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [chatMessages, setChatMessages] = useState({});
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [downloadingMessageId, setDownloadingMessageId] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null);
  const inquiryFileInputRef = React.useRef(null);

  // --- Order Chat States ---
  const [showOrderReplyChat, setShowOrderReplyChat] = useState(false);
  const [orderChatMessages, setOrderChatMessages] = useState([]);
  const [orderChatLoading, setOrderChatLoading] = useState(false);
  const [orderChatError, setOrderChatError] = useState(null);
  const [orderReplyMessage, setOrderReplyMessage] = useState("");
  const [orderSendingMessage, setOrderSendingMessage] = useState(false);
  const [orderAttachedFile, setOrderAttachedFile] = useState(null);
  const [orderUnreadCounts, setOrderUnreadCounts] = useState({});
  const [orderDownloadingMessageId, setOrderDownloadingMessageId] = useState(null);
  const orderFileInputRef = React.useRef(null);
  const orderChatBottomRef = React.useRef(null);
  const inquiryChatBottomRef = React.useRef(null);
  const orderChatPollingRef = React.useRef(null);
  const orderBadgePollingRef = React.useRef(null);
  const inquiryChatPollingRef = React.useRef(null);
  const inquiryBadgePollingRef = React.useRef(null);

  const agents = ["Agent A", "Agent B", "Agent C", "Unassigned"];

  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortField(null);
        setSortDirection("asc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1);
    setOrderCurrentPage(1);
  };

  const applySort = (data) => {
    if (!sortField) return data;
    const priorityOrder = { urgent: 1, high: 2, medium: 3, low: 4 };
    const statusOrder = { open: 1, "in-progress": 2, "in_progress": 2, "re-open": 3, closed: 4 };
    return [...data].sort((a, b) => {
      if (sortField === "priority") {
        const aV = priorityOrder[a.priority?.toLowerCase()] || 99;
        const bV = priorityOrder[b.priority?.toLowerCase()] || 99;
        return sortDirection === "asc" ? aV - bV : bV - aV;
      }
      if (sortField === "status") {
        const aV = statusOrder[a.status?.toLowerCase()] || 99;
        const bV = statusOrder[b.status?.toLowerCase()] || 99;
        return sortDirection === "asc" ? aV - bV : bV - aV;
      }
      if (sortField === "date") {
        return sortDirection === "asc"
          ? new Date(a.date) - new Date(b.date)
          : new Date(b.date) - new Date(a.date);
      }
      const aV = (a[sortField] || "").toString().toLowerCase();
      const bV = (b[sortField] || "").toString().toLowerCase();
      if (aV < bV) return sortDirection === "asc" ? -1 : 1;
      if (aV > bV) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <span style={{ opacity: 1, marginLeft: "4px", fontSize: "10px", color: "white" }}>▲▼</span>;
    return <span style={{ marginLeft: "4px", fontSize: "11px", color: "white" }}>{sortDirection === "asc" ? "▲" : "▼"}</span>;
  };

  // Scan all localStorage keys for a JWT token automatically
  const getToken = () => {
    const knownKeys = ["adminAuthToken", "authToken", "auth_token", "token", "accessToken", "jwt", "jwtToken", "adminToken", "userToken", "access_token"];
    for (const key of knownKeys) {
      const val = localStorage.getItem(key);
      if (val && val.startsWith("ey")) return val;
    }
    // Fallback: scan all keys for anything that looks like a JWT
    for (let i = 0; i < localStorage.length; i++) {
      const val = localStorage.getItem(localStorage.key(i));
      if (val && val.startsWith("ey") && val.includes(".")) return val;
    }
    return null;
  };

  const token = getToken();

  React.useEffect(() => {
    const t = getToken();
    if (t) {
      console.log("[Auth] Token found, length:", t.length);
    } else {
      console.warn("[Auth] No token found in localStorage. Chat features will be limited.");
      console.log("[Auth] Available localStorage keys:", Object.keys(localStorage));
    }
  }, []);

  const getClosedByLabel = (closedBy) => {
    if (!closedBy) return null;
    const val = closedBy.toUpperCase();
    if (val.includes("AUTO") || val.includes("SYSTEM")) return { label: "Auto", color: "#757575" };
    if (val.includes("USER") || val.includes("CUSTOMER")) return { label: "User", color: "#1976d2" };
    // Admin / Super Admin / Staff
    return { label: "Admin", color: "#d32f2f" };
  };

  // Maps backend enum values to human-readable names
  const mapAdminLabel = (val) => {
    if (!val) return null;
    const upper = val.toUpperCase().replace(/[\s_]/g, "");
    if (upper === "SUPERADMIN") return "Super Admin";
    if (upper === "ADMIN") return "Admin";
    if (upper === "USER" || upper === "CUSTOMER") return "User";
    if (upper === "SYSTEM" || upper === "AUTO") return "System";
    // Already human-readable (e.g. "Krishna Yadav") -- return as-is
    return val;
  };

  const getCurrentUserInfo = () => {
    const id = localStorage.getItem("adminUserId");
    const name = localStorage.getItem("adminName") || "Admin";
    return { id, name };
  };

  const getCurrentUserId = () => getCurrentUserInfo().id;

  useEffect(() => {
    fetchTicketsFromAPI();
  }, []);

  useEffect(() => {
    if (!defaultTab && location.state?.tab) {
      setActiveTab(location.state.tab);
    }
  }, [location.state, defaultTab]);

  useEffect(() => {
    if (activeTab === "order" && orderTickets.length === 0 && !orderLoading) {
      fetchOrderTickets();
    }
  }, [activeTab]);

  useEffect(() => {
    if (showOrderReplyChat || orderTickets.length === 0) {
      if (orderBadgePollingRef.current) {
        clearInterval(orderBadgePollingRef.current);
        orderBadgePollingRef.current = null;
      }
      return;
    }
    orderBadgePollingRef.current = setInterval(() => {
      orderTickets.forEach((ticket) => {
        fetchOrderUnreadCount(ticket.ticketNumber, ticket.id);
      });
    }, 5000);
    return () => {
      if (orderBadgePollingRef.current) {
        clearInterval(orderBadgePollingRef.current);
        orderBadgePollingRef.current = null;
      }
    };
  }, [showOrderReplyChat, orderTickets]);

  useEffect(() => {
    if (showReplyChat || tickets.length === 0) {
      if (inquiryBadgePollingRef.current) {
        clearInterval(inquiryBadgePollingRef.current);
        inquiryBadgePollingRef.current = null;
      }
      return;
    }
    inquiryBadgePollingRef.current = setInterval(() => {
      tickets.forEach((ticket) => {
        fetchUnreadCount(ticket.ticketNumber, ticket.id);
      });
    }, 5000);
    return () => {
      if (inquiryBadgePollingRef.current) {
        clearInterval(inquiryBadgePollingRef.current);
        inquiryBadgePollingRef.current = null;
      }
    };
  }, [showReplyChat, tickets]);

  useEffect(() => {
    return () => {
      if (orderChatPollingRef.current) clearInterval(orderChatPollingRef.current);
      if (orderBadgePollingRef.current) clearInterval(orderBadgePollingRef.current);
      if (inquiryChatPollingRef.current) clearInterval(inquiryChatPollingRef.current);
      if (inquiryBadgePollingRef.current) clearInterval(inquiryBadgePollingRef.current);
    };
  }, []);

  const fetchTicketsFromAPI = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        "https://car.amar.co.jp/api/dashboard/raise-ticket/get-all-tickets",
        {
          method: "GET",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();

      if (data.success && data.data) {
        const transformedTickets = data.data.map((ticket) => ({
          id: ticket.raiseTicketId,
          ticketNumber: ticket.ticketNumber,
          subject: ticket.subject || "--",
          description: ticket.message || "--",
          type: mapCategoryToType(ticket.category),
          status: (() => {
            const s = ticket.ticketStatus?.toUpperCase();
            if (s === "REOPEN" || s === "RE_OPEN" || s === "RE-OPEN") return "re-open";
            if (s === "OPEN" && (ticket.ticketReopenBy || ticket.ticketReopenByName)) return "re-open";
            if (s === "IN_PROGRESS") return "in_progress";
            if (s === "CLOSED") return "closed";
            return s ? s.toLowerCase() : "open";
          })(),
          date: convertDateFormat(ticket.lastActivityAt || ticket.creationDate),
          creationDate: ticket.creationDate,
          priority: ticket.priority ? ticket.priority.toLowerCase() : "medium",
          userName: `${ticket.userFirstName} ${ticket.userLastName}`,
          userEmail: ticket.userEmail || "--",
          carModel: ticket.carModelName || "N/A",
          category: ticket.category,
          ticketCategoryDisplay: ticket.ticketCategoryDisplay,
          ticketPriorityDisplay: ticket.ticketPriorityDisplay,
          attachmentPath: ticket.attachmentPath,
          attachmentFileName: ticket.attachmentFileName,
          shippingCalculationId: ticket.shippingCalculationId,
          inquiryRefNumber: ticket.inquiryRefNumber,
          alternativeMobileNumber: ticket.alternativeMobileNumber,
          countryCode: ticket.countryCode,
          reopenCount: ticket.reopenCount || 0,
          lastActivityAt: ticket.lastActivityAt,
          isRead: ticket.isRead,
          ticketClosedBy: ticket.ticketClosedByName || mapAdminLabel(ticket.ticketClosedBy),
          ticketClosedByRole: ticket.ticketClosedBy || null,
          ticketClosedAt: ticket.ticketClosedAt,
          reopenedBy: ticket.ticketReopenByName || ticket.reopenedBy || mapAdminLabel(ticket.ticketReopenBy) || null,
          reopenedByRole: ticket.ticketReopenBy || null,
          ticketReopenAt: ticket.ticketReopenAt || null,
          replies: 0,
          assignedTo: ticket.assignedAdminName || "Unassigned",
        }));
        setTickets(transformedTickets);

        transformedTickets.forEach((ticket) => {
          fetchUnreadCount(ticket.ticketNumber, ticket.id);
        });
      }
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setError(err.message || "Failed to fetch tickets");
    } finally {
      setLoading(false);
    }
  };

  // --- Close Inquiry Ticket ------------------------------------------------------
  const closeInquiryTicket = async (ticketNumber) => {
    try {
      setClosingInquiryTicket(true);
      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/raise-ticket/close?ticketNumber=${ticketNumber}`,
        {
          method: "PUT",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      if (data.success) {
        const closedByName = data.data?.ticketClosedByName || data.closedBy || data.data?.ticketClosedBy || null;
        setTickets((prev) =>
          prev.map((t) =>
            t.ticketNumber === ticketNumber
              ? {
                  ...t,
                  status: "closed",
                  ticketClosedBy: closedByName,
                  ticketClosedAt: data.closedAt || data.data?.ticketClosedAt,
                  lastActivityAt: data.closedAt || data.data?.lastActivityAt,
                }
              : t
          )
        );
        setSelectedTicket((prev) =>
          prev
            ? {
                ...prev,
                status: "closed",
                ticketClosedBy: closedByName,
                ticketClosedAt: data.closedAt || data.data?.ticketClosedAt,
                lastActivityAt: data.closedAt || data.data?.lastActivityAt,
              }
            : prev
        );
        setSuccessMessage("Ticket closed successfully.");
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 4000);
      }
    } catch (err) {
      console.error("Error closing ticket:", err);
      setSuccessMessage("Failed to close ticket: " + err.message);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 4000);
    } finally {
      setClosingInquiryTicket(false);
    }
  };

  // --- Reopen Inquiry Ticket -----------------------------------------------------
  const reopenInquiryTicket = async (ticketNumber) => {
    try {
      setReopeningInquiryTicket(true);
      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/raise-ticket/admin-reopen?ticketNumber=${ticketNumber}`,
        {
          method: "PUT",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      if (data.success) {
        const updatedData = data.data;
        const reopenedByName = data.data?.ticketReopenByName || data.reopenedBy || mapAdminLabel(data.data?.ticketReopenBy) || null;
        setTickets((prev) =>
          prev.map((t) =>
            t.ticketNumber === ticketNumber
              ? {
                  ...t,
                  status: "re-open",
                  ticketClosedBy: updatedData?.ticketClosedBy || null,
                  ticketClosedAt: updatedData?.ticketClosedAt || null,
                  reopenCount: data.reopenCount ?? updatedData?.reopenCount,
                  lastActivityAt: updatedData?.lastActivityAt,
                  isRead: updatedData?.isRead ?? t.isRead,
                  reopenedBy: reopenedByName,
                  ticketReopenAt: updatedData?.ticketReopenAt || null,
                }
              : t
          )
        );
        setSelectedTicket((prev) =>
          prev
            ? {
                ...prev,
                status: "re-open",
                ticketClosedBy: updatedData?.ticketClosedBy || null,
                ticketClosedAt: updatedData?.ticketClosedAt || null,
                reopenCount: data.reopenCount ?? updatedData?.reopenCount,
                lastActivityAt: updatedData?.lastActivityAt,
                isRead: updatedData?.isRead ?? prev.isRead,
                reopenedBy: reopenedByName,
                ticketReopenAt: updatedData?.ticketReopenAt || null,
              }
            : prev
        );
        setSuccessMessage(data.message || "Ticket reopened successfully.");
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 4000);
      }
    } catch (err) {
      console.error("Error reopening ticket:", err);
      setSuccessMessage("Failed to reopen ticket: " + err.message);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 4000);
    } finally {
      setReopeningInquiryTicket(false);
    }
  };

  // --- Fetch Order Tickets -----------------------------------------------------
  // const fetchOrderTickets = async () => {
  //   try {
  //     setOrderLoading(true);
  //     setOrderError(null);
  //     const response = await fetch(
  //       "https://car.amar.co.jp/api/dashboard/order-ticket/get-all-ticket",
  //       {
  //         method: "GET",
  //         headers: {
  //           accept: "application/json",
  //         },
  //       }
  //     );
  //     if (!response.ok) throw new Error(`API error: ${response.status}`);
  //     const data = await response.json();
  //     if (data.success && data.data) {
  //       const transformed = data.data.map((ticket) => ({
  //         id: ticket.orderRaiseTicketId,
  //         ticketNumber: ticket.orderTicketNumber,
  //         subject: ticket.ticketSubject,
  //         description: ticket.ticketDescription,
  //         category: ticket.ticketCategory,
  //         categoryDisplay: ticket.ticketCategoryDisplay,
  //         priority: ticket.ticketPriority.toLowerCase(),
  //         priorityDisplay: ticket.ticketPriorityDisplay,
  //         status: ticket.ticketStatus.toLowerCase(),
  //         statusDisplay: ticket.ticketStatusDisplay,
  //         date: convertDateFormat(ticket.creationDate),
  //         creationDate: ticket.creationDate,
  //         userName: `${ticket.userFirstName} ${ticket.userLastName}`,
  //         userEmail: ticket.userEmail,
  //         userId: ticket.userId,
  //         carModelName: ticket.carModelName,
  //         carModelId: ticket.carModelId,
  //         orderRefId: ticket.orderRefId,
  //         alternativeMobileNumber: ticket.alternativeMobileNumber,
  //         countryCode: ticket.countryCode,
  //         attachmentFileName: ticket.attachmentFileName,
  //         attachmentFileType: ticket.attachmentFileType,
  //         attachmentPath: ticket.attachmentPath,
  //         ticketClosedBy: ticket.ticketClosedBy,
  //         ticketClosedAt: ticket.ticketClosedAt,
  //         reopenCount: ticket.reopenCount,
  //         lastActivityAt: ticket.lastActivityAt,
  //         isRead: ticket.isRead,
  //       }));
  //       setOrderTickets(transformed);
  //       transformed.forEach((ticket) => {
  //         fetchOrderUnreadCount(ticket.ticketNumber, ticket.id);
  //       });
  //     }
  //   } catch (err) {
  //     console.error("Error fetching order tickets:", err);
  //     setOrderError(err.message || "Failed to fetch order tickets");
  //   } finally {
  //     setOrderLoading(false);
  //   }
  // };
  const fetchOrderTickets = async () => {
  try {
    setOrderLoading(true);
    setOrderError(null);
    const response = await fetch(
      "https://car.amar.co.jp/api/dashboard/order-ticket/get-all-ticket",
      {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    if (data.success && data.data) {
      const transformed = data.data.map((ticket) => {
        // Map enum values to display values
        const getStatusValue = (status, ticketData) => {
          if (!status) return "open";
          const s = status.toUpperCase().replace(/[-_\s]/g, "");
          if (s === "REOPEN") return "re-open";
          if (s === "OPEN" && (ticketData.ticketReopenBy || ticketData.ticketReopenByName)) return "re-open";
          if (s === "OPEN") return "open";
          if (s === "INPROGRESS") return "in-progress";
          if (s === "CLOSED") return "closed";
          return status.toLowerCase();
        };
        
        const getStatusDisplay = (status, ticketData) => {
          if (!status) return "Open";
          const s = status.toUpperCase().replace(/[-_\s]/g, "");
          if (s === "REOPEN") return "Re-Open";
          if (s === "OPEN" && (ticketData.ticketReopenBy || ticketData.ticketReopenByName)) return "Re-Open";
          if (s === "OPEN") return "Open";
          if (s === "INPROGRESS") return "In-Progress";
          if (s === "CLOSED") return "Closed";
          return status;
        };
        
        return {
          id: ticket.orderRaiseTicketId,
          ticketNumber: ticket.orderTicketNumber,
          subject: ticket.ticketSubject,
          description: ticket.ticketDescription,
          category: ticket.ticketCategory,
          categoryDisplay: ticket.ticketCategoryDisplay,
          priority: ticket.ticketPriority ? ticket.ticketPriority.toLowerCase() : "medium",
          priorityDisplay: ticket.ticketPriorityDisplay,
          status: getStatusValue(ticket.ticketStatus, ticket),  // "open", "in-progress", "closed", "re-open"
          statusDisplay: getStatusDisplay(ticket.ticketStatus, ticket),  // "Open", "In-Progress", "Closed", "Re-Open"
          date: convertDateFormat(ticket.lastActivityAt || ticket.creationDate),
          creationDate: ticket.creationDate,
          userName: `${ticket.userFirstName} ${ticket.userLastName}`,
          userEmail: ticket.userEmail,
          userId: ticket.userId,
          carModelName: ticket.carModelName,
          carModelId: ticket.carModelId,
          orderRefId: ticket.orderRefId,
          alternativeMobileNumber: ticket.alternativeMobileNumber,
          countryCode: ticket.countryCode,
          attachmentFileName: ticket.attachmentFileName,
          attachmentFileType: ticket.attachmentFileType,
          attachmentPath: ticket.attachmentPath,
          ticketClosedBy: ticket.ticketClosedByName || mapAdminLabel(ticket.ticketClosedBy),
          ticketClosedByRole: ticket.ticketClosedBy || null,
          ticketClosedAt: ticket.ticketClosedAt,
          reopenCount: ticket.reopenCount,
          lastActivityAt: ticket.lastActivityAt,
          isRead: ticket.isRead,
          reopenedBy: ticket.ticketReopenByName || ticket.reopenedBy || mapAdminLabel(ticket.ticketReopenBy) || null,
          reopenedByRole: ticket.ticketReopenBy || null,
          ticketReopenAt: ticket.ticketReopenAt || null,
          assignedTo: ticket.assignedAdminName || "Unassigned",
          assignedAdminId: ticket.assignedAdminId || null,
        };
      });
      setOrderTickets(transformed);
      transformed.forEach((ticket) => {
        fetchOrderUnreadCount(ticket.ticketNumber, ticket.id);
      });
    }
  } catch (err) {
    console.error("Error fetching order tickets:", err);
    setOrderError(err.message || "Failed to fetch order tickets");
  } finally {
    setOrderLoading(false);
  }
};

  // --- Close Order Ticket ------------------------------------------------------
  const closeOrderTicket = async (ticketNumber) => {
    try {
      setClosingOrderTicket(true);
      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/order-ticket/close?orderTicketNumber=${ticketNumber}`,
        {
          method: "PUT",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      if (data.success) {
        const closedByName = data.data?.ticketClosedByName || data.closedBy || mapAdminLabel(data.data?.ticketClosedBy) || null;
        setOrderTickets((prev) =>
          prev.map((t) =>
            t.ticketNumber === ticketNumber
              ? {
                  ...t,
                  status: "closed",
                  statusDisplay: "Closed",
                  ticketClosedBy: closedByName,
                  ticketClosedAt: data.closedAt || data.data?.ticketClosedAt,
                  lastActivityAt: data.closedAt || data.data?.lastActivityAt,
                }
              : t
          )
        );
        setSelectedOrderTicket((prev) =>
          prev
            ? {
                ...prev,
                status: "closed",
                statusDisplay: "Closed",
                ticketClosedBy: closedByName,
                ticketClosedAt: data.closedAt || data.data?.ticketClosedAt,
                lastActivityAt: data.closedAt || data.data?.lastActivityAt,
              }
            : prev
        );
        setSuccessMessage("Ticket closed successfully.");
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 4000);
      }
    } catch (err) {
      console.error("Error closing ticket:", err);
      setSuccessMessage("Failed to close ticket: " + err.message);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 4000);
    } finally {
      setClosingOrderTicket(false);
    }
  };

  // --- Reopen Order Ticket -----------------------------------------------------
  const reopenOrderTicket = async (ticketNumber) => {
    try {
      setReopeningOrderTicket(true);
      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/order-ticket/admin-reopen?orderTicketNumber=${ticketNumber}`,
        {
          method: "PUT",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      if (data.success) {
        const reopenedByName = data.data?.ticketReopenByName || data.reopenedBy || mapAdminLabel(data.data?.ticketReopenBy) || null;
        setOrderTickets((prev) =>
          prev.map((t) =>
            t.ticketNumber === ticketNumber
              ? {
                  ...t,
                  status: "re-open",
                  statusDisplay: "Re-Open",
                  ticketClosedBy: null,
                  ticketClosedAt: null,
                  reopenCount: data.reopenCount,
                  lastActivityAt: data.data?.lastActivityAt,
                  reopenedBy: reopenedByName,
                  ticketReopenAt: data.data?.ticketReopenAt || null,
                }
              : t
          )
        );
        setSelectedOrderTicket((prev) =>
          prev
            ? {
                ...prev,
                status: "re-open",
                statusDisplay: "Re-Open",
                ticketClosedBy: null,
                ticketClosedAt: null,
                reopenCount: data.reopenCount,
                lastActivityAt: data.data?.lastActivityAt,
                reopenedBy: reopenedByName,
                ticketReopenAt: data.data?.ticketReopenAt || null,
              }
            : prev
        );
        setSuccessMessage("Ticket reopened successfully.");
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 4000);
      }
    } catch (err) {
      console.error("Error reopening ticket:", err);
      setSuccessMessage("Failed to reopen ticket: " + err.message);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 4000);
    } finally {
      setReopeningOrderTicket(false);
    }
  };

  const fetchUnreadCount = async (ticketNumber, ticketId) => {
    const t = getToken();
    if (!t) return;
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/inquiry-chat/unread-count/${ticketNumber}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${t}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setUnreadCounts((prev) => ({ ...prev, [ticketId]: data.unreadCount || 0 }));
      }
    } catch (err) {
      // silent
    }
  };

  const markMessagesAsRead = async (ticketNumber, ticketId) => {
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/inquiry-chat/mark-read/${ticketNumber}`,
        {
          method: "PUT",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setChatMessages((prev) => ({
            ...prev,
            [ticketId]: (prev[ticketId] || []).map((msg) => ({
              ...msg,
              status: msg.isOwn ? msg.status : "read",
              isRead: true,
            })),
          }));
          setUnreadCounts((prev) => ({ ...prev, [ticketId]: 0 }));
        }
      }
    } catch (err) {
      console.error("Error marking messages as read:", err);
    }
  };

  const fetchChatHistory = async (ticketNumber, ticketId) => {
    try {
      setChatLoading(true);
      setChatError(null);
      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/inquiry-chat/history/${ticketNumber}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const chatData = await response.json();
      if (Array.isArray(chatData)) {
        const currentUserId = getCurrentUserId();
        const formattedMessages = chatData.map((msg) => ({
          id: msg.chatId,
          senderType: msg.senderType,
          senderName: msg.senderFullName,
          message: msg.message,
          timestamp: msg.formattedDateTime,
          isOwn: currentUserId
            ? String(msg.userId) === String(currentUserId)
            : msg.senderType === "ADMIN" || msg.senderType === "SUPERADMIN",
          isRead: msg.isRead || msg.readAt !== null,
          status: msg.readAt ? "read" : msg.isRead ? "read" : "delivered",
          attachmentFileName: msg.attachmentFileName,
          attachmentPath: msg.attachmentPath,
        }));
        setChatMessages((prev) => ({ ...prev, [ticketId]: formattedMessages }));
        // Update ticket date locally from latest message timestamp
        if (formattedMessages.length > 0) {
          const latestTimestamp = formattedMessages[formattedMessages.length - 1].timestamp;
          if (latestTimestamp) {
            setTickets((prev) =>
              prev.map((t) => t.id === ticketId ? { ...t, date: latestTimestamp } : t)
            );
          }
        }
        markMessagesAsRead(ticketNumber, ticketId);
      }
    } catch (err) {
      console.error("Error fetching chat history:", err);
      setChatError(err.message || "Failed to load chat history");
    } finally {
      setChatLoading(false);
    }
  };

  // --- Order Ticket Chat Functions ---------------------------------------------
  const fetchOrderUnreadCount = async (ticketNumber, ticketId) => {
    const t = getToken();
    if (!t) return;
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/order-ticket-chat/unread-count/${ticketNumber}`,
        { method: "GET", headers: { accept: "application/json", Authorization: `Bearer ${t}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setOrderUnreadCounts((prev) => ({ ...prev, [ticketId]: data.unreadCount || 0 }));
      }
    } catch (err) { /* silent */ }
  };

  const fetchOrderChatHistory = async (ticketNumber, ticketId) => {
    try {
      setOrderChatLoading(true);
      setOrderChatError(null);
      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/order-ticket-chat/history-by/${ticketNumber}`,
        { method: "GET", headers: { accept: "application/json", Authorization: `Bearer ${getToken()}` } }
      );
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const chatData = await response.json();
      if (Array.isArray(chatData)) {
        const currentUserId = getCurrentUserId();
        const formatted = chatData.map((msg) => ({
          id: msg.orderChatId ?? msg.chatId,
          senderType: msg.senderType,
          senderName: msg.senderFullName,
          message: msg.message,
          timestamp: msg.formattedDateTime,
          isOwn: currentUserId
            ? String(msg.userId) === String(currentUserId)
            : msg.senderType === "ADMIN" || msg.senderType === "SUPERADMIN",
          isRead: msg.isRead || msg.readAt !== null,
          status: msg.readAt ? "read" : msg.isRead ? "read" : "delivered",
          attachmentFileName: msg.attachmentFileName,
        }));
        setOrderChatMessages(formatted);
        // Update order ticket date locally from latest message timestamp
        if (ticketId && formatted.length > 0) {
          const latestTimestamp = formatted[formatted.length - 1].timestamp;
          if (latestTimestamp) {
            setOrderTickets((prev) =>
              prev.map((t) => t.id === ticketId ? { ...t, date: latestTimestamp } : t)
            );
          }
        }
      }
    } catch (err) {
      setOrderChatError(err.message || "Failed to load chat history");
    } finally {
      setOrderChatLoading(false);
    }
  };

  const markOrderMessagesAsRead = async (ticketNumber, ticketId) => {
    try {
      await fetch(
        `https://car.amar.co.jp/api/dashboard/order-ticket-chat/mark-read/${ticketNumber}`,
        { method: "PUT", headers: { accept: "application/json", Authorization: `Bearer ${getToken()}` } }
      );
      setOrderUnreadCounts((prev) => ({ ...prev, [ticketId]: 0 }));
    } catch (err) {
      console.error("Error marking order messages as read:", err);
    }
  };

  useEffect(() => {
    if (orderChatBottomRef.current) {
      orderChatBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [orderChatMessages]);

  useEffect(() => {
    if (inquiryChatBottomRef.current) {
      inquiryChatBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages, selectedTicket]);

  const handleOpenOrderChat = (ticket) => {
    setSelectedOrderTicket(ticket);
    setShowOrderReplyChat(true);
    setOrderAttachedFile(null);
    setOrderReplyMessage("");
    if (!selectedOrderTicket || selectedOrderTicket.id !== ticket.id) {
      setOrderChatMessages([]);
    }
    fetchOrderChatHistory(ticket.ticketNumber, ticket.id);
    markOrderMessagesAsRead(ticket.ticketNumber, ticket.id);

    setOrderTickets((prev) =>
      prev.map((t) => t.id === ticket.id ? { ...t, isRead: true } : t)
    );

    if (orderChatPollingRef.current) clearInterval(orderChatPollingRef.current);
    orderChatPollingRef.current = setInterval(async () => {
      try {
        const response = await fetch(
          `https://car.amar.co.jp/api/dashboard/order-ticket-chat/history-by/${ticket.ticketNumber}`,
          { method: "GET", headers: { accept: "application/json", Authorization: `Bearer ${getToken()}` } }
        );
        if (!response.ok) return;
        const chatData = await response.json();
        if (!Array.isArray(chatData)) return;
        const currentUserId = getCurrentUserId();
        const formatted = chatData.map((msg) => ({
          id: msg.orderChatId ?? msg.chatId,
          senderType: msg.senderType,
          senderName: msg.senderFullName,
          message: msg.message,
          timestamp: msg.formattedDateTime,
          isOwn: currentUserId
            ? String(msg.userId) === String(currentUserId)
            : msg.senderType === "ADMIN" || msg.senderType === "SUPERADMIN",
          isRead: msg.isRead || msg.readAt !== null,
          status: msg.readAt ? "read" : msg.isRead ? "read" : "delivered",
          attachmentFileName: msg.attachmentFileName,
        }));
        setOrderChatMessages((prev) => {
          const lastPrevId = prev[prev.length - 1]?.id;
          const lastNewId = formatted[formatted.length - 1]?.id;
          if (lastPrevId === lastNewId) return prev;
          return formatted;
        });
      } catch (err) { /* silent */ }
    }, 3000);
  };

  const handleCloseOrderChat = () => {
    if (selectedOrderTicket) {
      markOrderMessagesAsRead(selectedOrderTicket.ticketNumber, selectedOrderTicket.id);
    }
    if (orderChatPollingRef.current) {
      clearInterval(orderChatPollingRef.current);
      orderChatPollingRef.current = null;
    }
    setShowOrderReplyChat(false);
    setOrderReplyMessage("");
    setOrderAttachedFile(null);
  };

  const handleOrderFileChange = (e) => setOrderAttachedFile(e.target.files[0] || null);

  const handleSendOrderReply = async () => {
    if (!orderReplyMessage.trim() && !orderAttachedFile) return;
    if (!selectedOrderTicket) return;
    try {
      setOrderSendingMessage(true);
      const token = getToken();
      const formData = new FormData();
      if (orderAttachedFile) {
        formData.append("attachment", orderAttachedFile, orderAttachedFile.name);
      } else {
        formData.append("attachment", new Blob(), "");
      }
      const response = await axios.post(
        "https://car.amar.co.jp/api/dashboard/order-ticket-chat/send",
        formData,
        {
          params: {
            orderTicketNumber: selectedOrderTicket.ticketNumber,
            message: orderReplyMessage.trim(),
          },
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data.success) {
        const sent = response.data.chatData;
        const { name: adminName } = getCurrentUserInfo();
        const newMsg = {
          id: sent.orderChatId ?? sent.chatId,
          senderType: "SUPERADMIN",
          senderName: adminName,
          message: sent.message,
          timestamp: sent.formattedDateTime,
          isOwn: true,
          isRead: false,
          status: "sent",
          attachmentFileName: sent.attachmentFileName,
        };
        setOrderChatMessages((prev) => [...prev, newMsg]);
        setOrderReplyMessage("");
        setOrderAttachedFile(null);
        if (orderFileInputRef.current) orderFileInputRef.current.value = "";
        // Update order ticket date locally so sort order reflects latest activity without refresh
        setOrderTickets((prev) =>
          prev.map((t) =>
            t.id === selectedOrderTicket.id ? { ...t, date: sent.formattedDateTime || new Date().toISOString() } : t
          )
        );
        setSelectedOrderTicket((prev) => prev ? { ...prev, date: sent.formattedDateTime || new Date().toISOString() } : prev);
        // Agar order ticket open hai toh reply karte hi in-progress kar do (no reload needed)
        if (selectedOrderTicket.status === "open") {
          setOrderTickets((prev) =>
            prev.map((t) =>
              t.id === selectedOrderTicket.id ? { ...t, status: "in-progress" } : t
            )
          );
          setSelectedOrderTicket((prev) => prev ? { ...prev, status: "in-progress" } : prev);
        }
        setSuccessMessage("Message sent successfully!");
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (err) {
      setSuccessMessage("Failed to send message: " + (err.response?.data?.message || err.message));
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 4000);
    } finally {
      setOrderSendingMessage(false);
    }
  };

  const handleDownloadOrderAttachment = async (chatId, fileName) => {
    if (!chatId || !fileName) return;
    try {
      setOrderDownloadingMessageId(chatId);
      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/order-ticket-chat/download/${chatId}`,
        { method: "GET", headers: { accept: "application/json", Authorization: `Bearer ${getToken()}` } }
      );
      if (response.status === 404) throw new Error("No attachment found.");
      if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccessMessage(`${fileName} downloaded successfully!`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      setSuccessMessage("Failed to download: " + err.message);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 4000);
    } finally {
      setOrderDownloadingMessageId(null);
    }
  };

  const handleDownloadAttachment = async (chatId, fileName) => {
    if (!chatId || !fileName) return;
    try {
      setDownloadingMessageId(chatId);
      setSuccessMessage(`Downloading ${fileName}...`);
      setShowSuccess(true);
      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/inquiry-chat/download/${chatId}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      if (response.status === 404) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "No attachment found.");
      }
      if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccessMessage(`${fileName} downloaded successfully!`);
    } catch (err) {
      setSuccessMessage("Failed to download: " + err.message);
    } finally {
      setDownloadingMessageId(null);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 4000);
    }
  };

  const mapCategoryToType = (category) => {
    const categoryMap = {
      TECHNICAL_ISSUE: "technical",
      ORDER_ISSUE: "order",
      GENERAL_INQUIRY: "inquiry",
      BILLING_QUESTION: "billing",
      FEEDBACK_SUGGESTION: "feedback",
      OTHER: "inquiry",
    };
    return categoryMap[category] || "inquiry";
  };

  const convertDateFormat = (dateString) => {
    if (!dateString) return new Date().toISOString().split("T")[0];
    try {
      const [datePart, timePart] = dateString.split(" ");
      const [day, month, year] = datePart.split("-");
      // Append Z to treat as UTC and avoid timezone shift
      return timePart ? `${year}-${month}-${day}T${timePart}Z` : `${year}-${month}-${day}`;
    } catch {
      return new Date().toISOString().split("T")[0];
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      resolved: "#4caf50",
      "in-progress": "#2196f3",
      open: "#ff9800",
      pending: "#f44336",
      closed: "#4caf50",
      "re-open": "#9c27b0",
    };
    return colors[status] || "#757575";
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: "#f44336",
      medium: "#ff9800",
      low: "#4caf50",
      urgent: "#5a5a5a",
    };
    return colors[priority] || "#757575";
  };

  const getTypeText = (type) => {
    const typeMap = {
      order: "Order Issue",
      inquiry: "Inquiry",
      technical: "Technical",
      billing: "Billing",
      feedback: "Feedback",
    };
    return typeMap[type] || "Inquiry";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "--";
    const date = new Date(dateString);
    const options = { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false };
    return date.toLocaleString("en-GB", options);
  };

  const formatChatTime = (dateString) => {
    if (!dateString) return "Just now";
    const date = new Date(dateString);
    const options = { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false };
    return date.toLocaleString("en-GB", options);
  };

  const isUserRole = (role) => {
    if (!role) return false;
    const r = role.toUpperCase().replace(/[\s_-]/g, "");
    return r === "USER" || r === "CUSTOMER";
  };

  const renderActorBadge = (name, role, color) => {
    if (!name) return null;
    const isUser = isUserRole(role);
    return (
      <div style={{ marginTop: "4px", fontSize: "11px", color, fontWeight: "600", whiteSpace: "nowrap", display: "flex", alignItems: "center", justifyContent: "center", gap: "3px" }}>
        {isUser ? (
          <PersonIcon style={{ fontSize: "24px" }} />
        ) : (
          <SupportAgentIcon style={{ fontSize: "24px" }} />
        )}
        {name}
      </div>
    );
  };

  const MessageTick = ({ status }) => {
    if (status === "read")
      return <span style={{ fontSize: "12px", color: "#2196f3", marginLeft: "4px" }}>vv</span>;
    if (status === "delivered")
      return <span style={{ fontSize: "12px", color: "#999", marginLeft: "4px" }}>vv</span>;
    return <span style={{ fontSize: "12px", color: "#999", marginLeft: "4px" }}>v</span>;
  };

  const handleUpdateTicket = () => {
    const updatedTickets = tickets.map((t) =>
      t.id === selectedTicket.id
        ? { ...t, status: updatedStatus || t.status, assignedTo: updatedAssignee || t.assignedTo }
        : t
    );
    setTickets(updatedTickets);
    setSelectedTicket(updatedTickets.find((t) => t.id === selectedTicket.id));
    setShowUpdate(false);
    setSuccessMessage("Ticket updated successfully!");
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const handleOpenChat = (ticket) => {
    setSelectedTicket(ticket);
    setShowReplyChat(true);
    setAttachedFile(null);
    setReplyMessage("");
    fetchChatHistory(ticket.ticketNumber, ticket.id);

    setTickets((prev) =>
      prev.map((t) => t.id === ticket.id ? { ...t, isRead: true } : t)
    );

    if (inquiryChatPollingRef.current) clearInterval(inquiryChatPollingRef.current);
    inquiryChatPollingRef.current = setInterval(async () => {
      try {
        const response = await fetch(
          `https://car.amar.co.jp/api/dashboard/inquiry-chat/history/${ticket.ticketNumber}`,
          { method: "GET", headers: { accept: "application/json", Authorization: `Bearer ${getToken()}` } }
        );
        if (!response.ok) return;
        const chatData = await response.json();
        if (!Array.isArray(chatData)) return;
        const currentUserId = getCurrentUserId();
        const formatted = chatData.map((msg) => ({
          id: msg.chatId,
          senderType: msg.senderType,
          senderName: msg.senderFullName,
          message: msg.message,
          timestamp: msg.formattedDateTime,
          isOwn: currentUserId
            ? String(msg.userId) === String(currentUserId)
            : msg.senderType === "ADMIN" || msg.senderType === "SUPERADMIN",
          isRead: msg.isRead || msg.readAt !== null,
          status: msg.readAt ? "read" : msg.isRead ? "read" : "delivered",
          attachmentFileName: msg.attachmentFileName,
          attachmentPath: msg.attachmentPath,
        }));
        setChatMessages((prev) => {
          const prevMsgs = prev[ticket.id] || [];
          const lastPrevId = prevMsgs[prevMsgs.length - 1]?.id;
          const lastNewId = formatted[formatted.length - 1]?.id;
          if (lastPrevId === lastNewId) return prev;
          return { ...prev, [ticket.id]: formatted };
        });
      } catch (err) { /* silent */ }
    }, 3000);
  };

  const handleCloseInquiryChat = () => {
    if (selectedTicket) {
      markMessagesAsRead(selectedTicket.ticketNumber, selectedTicket.id);
    }
    if (inquiryChatPollingRef.current) {
      clearInterval(inquiryChatPollingRef.current);
      inquiryChatPollingRef.current = null;
    }
    setShowReplyChat(false);
    setSelectedTicket(null);
    setReplyMessage("");
    setAttachedFile(null);
    if (inquiryFileInputRef.current) inquiryFileInputRef.current.value = "";
  };

  const handleFileChange = (e) => setAttachedFile(e.target.files[0] || null);

  const handleSendReply = async () => {
    if (!replyMessage.trim() && !attachedFile) return;
    if (!selectedTicket) return;
    try {
      setSendingMessage(true);
      const formData = new FormData();
      formData.append("ticketNumber", selectedTicket.ticketNumber);
      formData.append("message", replyMessage.trim());
      if (attachedFile) formData.append("attachment", attachedFile, attachedFile.name);
      const response = await fetch(
        "https://car.amar.co.jp/api/dashboard/inquiry-chat/send",
        {
          method: "POST",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: formData,
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API error: ${response.status}`);
      }
      const data = await response.json();
      if (data.success && data.chatData) {
        const newMessage = {
          id: data.chatData.chatId,
          senderType: data.chatData.senderType,
          senderName: data.chatData.senderFullName,
          message: data.chatData.message,
          timestamp: data.chatData.formattedDateTime,
          isOwn: true,
          isRead: data.chatData.isRead || false,
          status: "sent",
          attachmentFileName: data.chatData.attachmentFileName,
          attachmentPath: data.chatData.attachmentPath,
        };
        setChatMessages((prev) => ({
          ...prev,
          [selectedTicket.id]: [...(prev[selectedTicket.id] || []), newMessage],
        }));
        // Update ticket date locally so sort order reflects latest activity without refresh
        setTickets((prev) =>
          prev.map((t) =>
            t.id === selectedTicket.id ? { ...t, date: data.chatData.formattedDateTime || new Date().toISOString() } : t
          )
        );
        setSelectedTicket((prev) => prev ? { ...prev, date: data.chatData.formattedDateTime || new Date().toISOString() } : prev);
        // Agar ticket open hai toh reply karte hi in-progress kar do (no reload needed)
        if (selectedTicket.status === "open") {
          setTickets((prev) =>
            prev.map((t) =>
              t.id === selectedTicket.id ? { ...t, status: "in-progress" } : t
            )
          );
          setSelectedTicket((prev) => prev ? { ...prev, status: "in-progress" } : prev);
        }
        setReplyMessage("");
        setAttachedFile(null);
        if (inquiryFileInputRef.current) inquiryFileInputRef.current.value = "";
        setSuccessMessage("Message sent successfully!");
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (err) {
      setSuccessMessage("Failed to send message: " + err.message);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 4000);
    } finally {
      setSendingMessage(false);
    }
  };

  // --- Filtered Inquiry Tickets ------------------------------------------------
  const filteredTickets = tickets
    .filter((ticket) => {
      const matchStatus = filterStatus === "all" || ticket.status === filterStatus;
      const matchPriority = filterPriority === "all" || ticket.priority === filterPriority;
      const matchType = filterType === "all" || ticket.type === filterType;
      const matchSearch =
        searchText === "" ||
        ticket.ticketNumber.toLowerCase().includes(searchText.toLowerCase()) ||
        ticket.subject.toLowerCase().includes(searchText.toLowerCase()) ||
        ticket.userName.toLowerCase().includes(searchText.toLowerCase()) ||
        ticket.userEmail.toLowerCase().includes(searchText.toLowerCase());
      return matchStatus && matchPriority && matchType && matchSearch;
    })
    .sort((a, b) => {
      const unreadA = (unreadCounts[a.id] || 0) > 0 ? 1 : 0;
      const unreadB = (unreadCounts[b.id] || 0) > 0 ? 1 : 0;
      if (unreadB !== unreadA) return unreadB - unreadA;
      // Secondary: latest activity first
      return new Date(b.date) - new Date(a.date);
    });

  const sortedFilteredTickets = applySort(filteredTickets);

  const totalPages = Math.ceil(sortedFilteredTickets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTickets = sortedFilteredTickets.slice(startIndex, startIndex + itemsPerPage);

  // --- Filtered Order Tickets --------------------------------------------------
  const filteredOrderTickets = orderTickets
    .filter((ticket) => {
      const matchStatus = orderFilterStatus === "all" || ticket.status === orderFilterStatus;
      const matchPriority = orderFilterPriority === "all" || ticket.priority === orderFilterPriority;
      const matchCategory = orderFilterCategory === "all" || ticket.category === orderFilterCategory;
      const matchSearch =
        orderSearchText === "" ||
        ticket.ticketNumber.toLowerCase().includes(orderSearchText.toLowerCase()) ||
        ticket.subject.toLowerCase().includes(orderSearchText.toLowerCase()) ||
        ticket.userName.toLowerCase().includes(orderSearchText.toLowerCase()) ||
        ticket.orderRefId?.toLowerCase().includes(orderSearchText.toLowerCase()) ||
        ticket.userEmail.toLowerCase().includes(orderSearchText.toLowerCase());
      return matchStatus && matchPriority && matchCategory && matchSearch;
    })
    .sort((a, b) => {
      const unreadA = (orderUnreadCounts[a.id] || 0) > 0 ? 1 : 0;
      const unreadB = (orderUnreadCounts[b.id] || 0) > 0 ? 1 : 0;
      if (unreadB !== unreadA) return unreadB - unreadA;
      // Secondary: latest activity first
      return new Date(b.date) - new Date(a.date);
    });

  const sortedFilteredOrderTickets = applySort(filteredOrderTickets);

  const orderTotalPages = Math.ceil(sortedFilteredOrderTickets.length / orderItemsPerPage);
  const orderStartIndex = (orderCurrentPage - 1) * orderItemsPerPage;
  const paginatedOrderTickets = sortedFilteredOrderTickets.slice(
    orderStartIndex,
    orderStartIndex + orderItemsPerPage
  );

  // --- Shared styles -----------------------------------------------------------
  const selectStyle = {
    width: "100%",
    padding: "10px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "13px",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "6px",
    fontSize: "12px",
    color: "#666",
    fontWeight: "600",
  };

  const paginationBtnStyle = (active, disabled) => ({
    padding: "6px 10px",
    backgroundColor: active ? "#d32f2f" : disabled ? "#f0f0f0" : "#fff",
    color: active ? "white" : disabled ? "#ccc" : "#d32f2f",
    border: "1px solid #d32f2f",
    borderRadius: "4px",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: "13px",
    fontWeight: active ? "600" : "400",
    minWidth: "32px",
  });

  // --- Render pagination -------------------------------------------------------
  const renderPagination = (curPage, setCurPage, totalPg, perPage, setPerPage) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "20px 0",
        borderTop: "1px solid #f0f0f0",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <span style={{ fontSize: "13px", color: "#666" }}>Results per page:</span>
        <select
          value={perPage}
          onChange={(e) => { setPerPage(parseInt(e.target.value)); setCurPage(1); }}
          style={{ padding: "6px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", cursor: "pointer" }}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
        </select>
      </div>
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        <button
          onClick={() => setCurPage(Math.max(1, curPage - 1))}
          disabled={curPage === 1}
          style={paginationBtnStyle(false, curPage === 1)}
        >&lt;</button>
        {Array.from({ length: totalPg }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            onClick={() => setCurPage(page)}
            style={paginationBtnStyle(curPage === page, false)}
          >{page}</button>
        ))}
        <button
          onClick={() => setCurPage(Math.min(totalPg, curPage + 1))}
          disabled={curPage === totalPg || totalPg === 0}
          style={paginationBtnStyle(false, curPage === totalPg || totalPg === 0)}
        >&gt;</button>
      </div>
    </div>
  );

  // ============================================================================
  return (
    <div style={{ width: "100%", backgroundColor: "#f5f5f5", padding: "30px 20px" }}>
      <h2 style={{ margin: "0 0 30px 0", fontSize: "1.4rem", fontWeight: 700, color: "#0f172a" }}>
        Support Tickets Management
        <span style={{ display: "block", width: 36, height: 3, background: "#e11d48", borderRadius: 2, marginTop: 6 }}></span>
      </h2>
      <div
        style={{
          backgroundColor: "#fff",
          border: "2px solid #EBEBEB",
          borderRadius: "10px",
          padding: "30px",
        }}
      >
        {/* -- TABS -- */}
        {defaultTab ? (
          <div style={{ marginBottom: "28px" }}>
            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#333" }}>
              {defaultTab === "order" ? "Order Tickets" : "Inquiry Tickets"}
              {" "}
              <span style={{ fontSize: "14px", fontWeight: "500", color: "#999" }}>
                ({defaultTab === "order" ? orderTickets.length : tickets.length})
              </span>
            </h2>
          </div>
        ) : (
        <div style={{ display: "flex", borderBottom: "2px solid #EBEBEB", marginBottom: "28px" }}>
          {[
            { key: "inquiry", label: "Inquiry Tickets", count: tickets.length },
            { key: "order", label: "Order Tickets", count: orderTickets.length },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSortField(null); setSortDirection("asc"); }}
              style={{
                padding: "12px 28px",
                border: "none",
                borderBottom: activeTab === tab.key ? "3px solid #d32f2f" : "3px solid transparent",
                backgroundColor: "transparent",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: activeTab === tab.key ? "700" : "500",
                color: activeTab === tab.key ? "#d32f2f" : "#666",
                marginBottom: "-2px",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {tab.label}
              {tab.count > 0 && (
                <span
                  style={{
                    backgroundColor: activeTab === tab.key ? "#d32f2f" : "#ddd",
                    color: activeTab === tab.key ? "white" : "#666",
                    borderRadius: "12px",
                    padding: "2px 8px",
                    fontSize: "11px",
                    fontWeight: "600",
                  }}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
        )}

        {/* ======================================================
            INQUIRY TICKETS TAB
        ====================================================== */}
        {activeTab === "inquiry" && (
          <>
            {loading && (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <p style={{ fontSize: "16px", color: "#666", marginBottom: "16px" }}>Loading tickets...</p>
                <div style={{ display: "inline-block", width: "40px", height: "40px", border: "4px solid #f0f0f0", borderTop: "4px solid #d32f2f", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
              </div>
            )}

            {error && (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <p style={{ fontSize: "14px", color: "#d32f2f", marginBottom: "16px" }}>!️ Error: {error}</p>
                <button onClick={fetchTicketsFromAPI} style={{ padding: "10px 20px", backgroundColor: "#d32f2f", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>Retry</button>
              </div>
            )}

            {!loading && !error && (
              <>
                {/* Filters */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "25px", padding: "20px", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
                  <div>
                    <label style={labelStyle}>SEARCH</label>
                    <input type="text" value={searchText} onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }} placeholder="Search by ticket, subject, user..." style={{ ...selectStyle }} />
                  </div>
                  <div>
                    <label style={labelStyle}>TYPE</label>
                    <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }} style={selectStyle}>
                      <option value="all">All Types</option>
                      <option value="inquiry">Inquiry</option>
                      <option value="order">Order Issue</option>
                      <option value="technical">Technical</option>
                      <option value="billing">Billing</option>
                      <option value="feedback">Feedback</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>STATUS</label>
                    <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={selectStyle}>
                      <option value="all">All Status</option>
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="re-open">Re-Open</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>PRIORITY</label>
                    <select value={filterPriority} onChange={(e) => { setFilterPriority(e.target.value); setCurrentPage(1); }} style={selectStyle}>
                      <option value="all">All Priority</option>
                      <option value="urgent">Urgent</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>

                {sortedFilteredTickets.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "60px 20px" }}>
                    <h3 style={{ color: "#999" }}>No tickets found</h3>
                    <p style={{ color: "#ccc", fontSize: "13px" }}>Try adjusting your filters or search criteria</p>
                  </div>
                ) : (
                  <>
                    <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "70vh", marginBottom: "20px" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", fontFamily: "Roboto, sans-serif" }}>
                        <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                          <tr style={{ backgroundColor: "#d32f2f", color: "white" }}>
                            <th onClick={() => handleSort("ticketNumber")} style={{ padding: "15px 12px", textAlign: "left", fontWeight: "600", fontSize: "12px", backgroundColor: "#d32f2f", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>Ticket #{getSortIcon("ticketNumber")}</th>
                            <th style={{ padding: "15px 12px", textAlign: "left", fontWeight: "600", fontSize: "12px", backgroundColor: "#d32f2f" }}>Subject</th>
                            <th onClick={() => handleSort("userName")} style={{ padding: "15px 12px", textAlign: "left", fontWeight: "600", fontSize: "12px", backgroundColor: "#d32f2f", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>Customer{getSortIcon("userName")}</th>
                            <th onClick={() => handleSort("inquiryRefNumber")} style={{ padding: "15px 12px", textAlign: "left", fontWeight: "600", fontSize: "12px", backgroundColor: "#d32f2f", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>Inq. Ref{getSortIcon("inquiryRefNumber")}</th>
                            <th onClick={() => handleSort("type")} style={{ padding: "15px 12px", textAlign: "center", fontWeight: "600", fontSize: "12px", backgroundColor: "#d32f2f", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>Type{getSortIcon("type")}</th>
                            <th onClick={() => handleSort("status")} style={{ padding: "15px 12px", textAlign: "center", fontWeight: "600", fontSize: "12px", backgroundColor: "#d32f2f", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>Status{getSortIcon("status")}</th>
                            <th onClick={() => handleSort("priority")} style={{ padding: "15px 12px", textAlign: "center", fontWeight: "600", fontSize: "12px", backgroundColor: "#d32f2f", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>Priority{getSortIcon("priority")}</th>
                            <th onClick={() => handleSort("date")} style={{ padding: "15px 12px", textAlign: "center", fontWeight: "600", fontSize: "12px", backgroundColor: "#d32f2f", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>Last Update{getSortIcon("date")}</th>
                            <th style={{ padding: "15px 12px", textAlign: "center", fontWeight: "600", fontSize: "12px", backgroundColor: "#d32f2f", whiteSpace: "nowrap" }}>Assigned To</th>
                            <th style={{ padding: "15px 12px", textAlign: "center", fontWeight: "600", fontSize: "12px", backgroundColor: "#d32f2f" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedTickets.map((ticket) => (
                            <tr
                              key={ticket.id}
                              className={(!ticket.isRead || (unreadCounts[ticket.id] || 0) > 0) ? "ticket-row-active" : "ticket-row-read"}
                              style={{
                                borderBottom: "1px solid #f0f0f0",
                                cursor: "pointer",
                                ...(ticket.status === "open" ? { backgroundColor: "#fff3e0", borderLeft: "4px solid #ff9800", borderRadius: 0 } : {}),
                              }}
                              onClick={() => { setSelectedTicket(ticket); setUpdatedStatus(ticket.status); setUpdatedAssignee(ticket.assignedTo); setShowDetails(true); }}
                              onMouseEnter={(e) => e.currentTarget.classList.add("ticket-row-hover")}
                              onMouseLeave={(e) => e.currentTarget.classList.remove("ticket-row-hover")}
                            >
                              <td style={{ padding: "14px 12px", fontWeight: "600", color: "#333" }}>
                                {ticket.status === "open" && (
                                  <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#ff9800", marginRight: "6px" }}></span>
                                )}
                                {ticket.ticketNumber}
                              </td>
                              <td style={{ padding: "14px 12px" }}>
                                <div style={{ fontWeight: "500", color: "#333", fontSize: "13px" }}>{ticket.subject.substring(0, 30)}</div>
                                <div style={{ fontSize: "11px", color: "#999" }}>ID: {ticket.id}</div>
                              </td>
                              <td style={{ padding: "14px 12px" }}>
                                <div style={{ fontWeight: "500", fontSize: "13px", color: "#333" }}>{ticket.userName}</div>
                                <div style={{ fontSize: "11px", color: "#999" }}>{ticket.userEmail}</div>
                              </td>
                              <td style={{ padding: "14px 12px" }}>
                                <span style={{ fontSize: "12px", fontWeight: "600", color: "#555", fontFamily: "monospace" }}>{ticket.inquiryRefNumber || "--"}</span>
                              </td>
                              <td style={{ padding: "14px 12px", textAlign: "center" }}>
                                <span style={{ backgroundColor: "#e3f2fd", color: "#1976d2", padding: "5px 11px", borderRadius: "4px", fontSize: "11px", fontWeight: "600" }}>{getTypeText(ticket.type)}</span>
                              </td>
                              <td style={{ padding: "14px 12px", textAlign: "center" }}>
                                <span style={{ backgroundColor: getStatusColor(ticket.status), color: "white", padding: "5px 11px", borderRadius: "4px", fontSize: "11px", fontWeight: "600" }}>{ticket.status}</span>
                                {ticket.status === "closed" && ticket.ticketClosedBy && renderActorBadge(ticket.ticketClosedBy, ticket.ticketClosedByRole, "#d32f2f")}
                                {ticket.status === "re-open" && ticket.reopenedBy && renderActorBadge(ticket.reopenedBy, ticket.reopenedByRole, "#9c27b0")}
                              </td>
                              <td style={{ padding: "14px 12px", textAlign: "center" }}>
                                <span style={{ backgroundColor: getPriorityColor(ticket.priority), color: "white", padding: "5px 11px", borderRadius: "4px", fontSize: "11px", fontWeight: "600" }}>{ticket.priority}</span>
                              </td>
                              <td style={{ padding: "14px 12px", textAlign: "center", color: "#333" }}>📅 {formatDate(ticket.date)}</td>
                              <td style={{ padding: "14px 12px", textAlign: "center", fontSize: "12px", color: ticket.assignedTo && ticket.assignedTo !== "Unassigned" ? "#333" : "#999" }}>{ticket.assignedTo || "Unassigned"}</td>
                              <td style={{ padding: "14px 12px", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                <button onClick={(e) => { e.stopPropagation(); setSelectedTicket(ticket); setUpdatedStatus(ticket.status); setUpdatedAssignee(ticket.assignedTo); setShowDetails(true); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#1976d2", fontSize: "16px", padding: "4px 8px", marginRight: "8px" }} title="View Details">👁️</button>
                                <div style={{ display: "inline-block", position: "relative" }}>
                                  <button onClick={(e) => { e.stopPropagation(); handleOpenChat(ticket); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#d32f2f", fontSize: "16px", padding: "4px 8px" }} title="View Chat">💬</button>
                                  {unreadCounts[ticket.id] > 0 && (
                                    <span style={{ position: "absolute", top: "-8px", right: "-8px", backgroundColor: "#d32f2f", color: "white", borderRadius: "50%", width: "20px", height: "20px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "bold" }}>{unreadCounts[ticket.id]}</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {renderPagination(currentPage, setCurrentPage, totalPages, itemsPerPage, setItemsPerPage)}
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* ======================================================
            ORDER TICKETS TAB
        ====================================================== */}
        {activeTab === "order" && (
          <>
            {orderLoading && (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <p style={{ fontSize: "16px", color: "#666", marginBottom: "16px" }}>Loading order tickets...</p>
                <div style={{ display: "inline-block", width: "40px", height: "40px", border: "4px solid #f0f0f0", borderTop: "4px solid #d32f2f", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
              </div>
            )}

            {orderError && (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <p style={{ fontSize: "14px", color: "#d32f2f", marginBottom: "16px" }}>!️ Error: {orderError}</p>
                <button onClick={fetchOrderTickets} style={{ padding: "10px 20px", backgroundColor: "#d32f2f", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>Retry</button>
              </div>
            )}

            {!orderLoading && !orderError && (
              <>
                {/* Order Filters */}
                {/* <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "25px", padding: "20px", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
                  <div>
                    <label style={labelStyle}>SEARCH</label>
                    <input type="text" value={orderSearchText} onChange={(e) => { setOrderSearchText(e.target.value); setOrderCurrentPage(1); }} placeholder="Search by ticket, order ref, user..." style={{ ...selectStyle }} />
                  </div>
                  <div>
                    <label style={labelStyle}>CATEGORY</label>
                    <select value={orderFilterCategory} onChange={(e) => { setOrderFilterCategory(e.target.value); setOrderCurrentPage(1); }} style={selectStyle}>
                      <option value="all">All Categories</option>
                      <option value="PRODUCT_ISSUE">Product Issue</option>
                      <option value="RETURN_REQUEST">Return Request</option>
                      <option value="ORDER_STATUS">Order Status</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>STATUS</label>
                    <select value={orderFilterStatus} onChange={(e) => { setOrderFilterStatus(e.target.value); setOrderCurrentPage(1); }} style={selectStyle}>
                      <option value="all">All Status</option>
                      <option value="open">Open</option>
                      <option value="in-progress">In-Progress</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>PRIORITY</label>
                    <select value={orderFilterPriority} onChange={(e) => { setOrderFilterPriority(e.target.value); setOrderCurrentPage(1); }} style={selectStyle}>
                      <option value="all">All Priority</option>
                      <option value="urgent">Urgent</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div> */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "25px", padding: "20px", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
                  <div>
                    <label style={labelStyle}>SEARCH</label>
                    <input type="text" value={orderSearchText} onChange={(e) => { setOrderSearchText(e.target.value); setOrderCurrentPage(1); }} placeholder="Search by ticket, order ref, user..." style={{ ...selectStyle }} />
                  </div>
                  <div>
                    <label style={labelStyle}>CATEGORY</label>
                    <select value={orderFilterCategory} onChange={(e) => { setOrderFilterCategory(e.target.value); setOrderCurrentPage(1); }} style={selectStyle}>
                      <option value="all">All Categories</option>
                      <option value="PRODUCT_ISSUE">Product Issue</option>
                      <option value="RETURN_REQUEST">Return Request</option>
                      <option value="ORDER_STATUS">Order Status</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>STATUS</label>
                    <select value={orderFilterStatus} onChange={(e) => { setOrderFilterStatus(e.target.value); setOrderCurrentPage(1); }} style={selectStyle}>
                      <option value="all">All Status</option>
                      <option value="open">Open</option>
                      <option value="in-progress">In-Progress</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>PRIORITY</label>
                    <select value={orderFilterPriority} onChange={(e) => { setOrderFilterPriority(e.target.value); setOrderCurrentPage(1); }} style={selectStyle}>
                      <option value="all">All Priority</option>
                      <option value="urgent">Urgent</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>

                {sortedFilteredOrderTickets.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "60px 20px" }}>
                    <h3 style={{ color: "#999" }}>No order tickets found</h3>
                    <p style={{ color: "#ccc", fontSize: "13px" }}>Try adjusting your filters or search criteria</p>
                  </div>
                ) : (
                  <>
                    <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "70vh", marginBottom: "20px" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", fontFamily: "Roboto, sans-serif" }}>
                        <thead>
                          <tr style={{ backgroundColor: "#d32f2f", color: "white" }}>
                            <th onClick={() => handleSort("ticketNumber")} style={{ padding: "15px 12px", textAlign: "left", fontWeight: "600", fontSize: "12px", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>Ticket#{getSortIcon("ticketNumber")}</th>
                            <th style={{ padding: "15px 12px", textAlign: "left", fontWeight: "600", fontSize: "12px", whiteSpace: "nowrap" }}>Subject</th>
                            <th onClick={() => handleSort("userName")} style={{ padding: "15px 12px", textAlign: "left", fontWeight: "600", fontSize: "12px", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>Customer{getSortIcon("userName")}</th>
                            <th onClick={() => handleSort("orderRefId")} style={{ padding: "15px 12px", textAlign: "left", fontWeight: "600", fontSize: "12px", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>Order Ref{getSortIcon("orderRefId")}</th>
                            <th onClick={() => handleSort("categoryDisplay")} style={{ padding: "15px 12px", textAlign: "center", fontWeight: "600", fontSize: "12px", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>Category{getSortIcon("categoryDisplay")}</th>
                            <th onClick={() => handleSort("status")} style={{ padding: "15px 12px", textAlign: "center", fontWeight: "600", fontSize: "12px", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>Status{getSortIcon("status")}</th>
                            <th onClick={() => handleSort("priority")} style={{ padding: "15px 12px", textAlign: "center", fontWeight: "600", fontSize: "12px", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>Priority{getSortIcon("priority")}</th>
                            <th onClick={() => handleSort("date")} style={{ padding: "15px 12px", textAlign: "center", fontWeight: "600", fontSize: "12px", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>Last Update{getSortIcon("date")}</th>
                            <th style={{ padding: "15px 12px", textAlign: "center", fontWeight: "600", fontSize: "12px", whiteSpace: "nowrap" }}>Assigned To</th>
                            <th style={{ padding: "15px 12px", textAlign: "center", fontWeight: "600", fontSize: "12px", whiteSpace: "nowrap" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedOrderTickets.map((ticket) => (
                            <tr
                              key={ticket.id}
                              className={(!ticket.isRead || (orderUnreadCounts[ticket.id] || 0) > 0) ? "ticket-row-active" : "ticket-row-read"}
                              style={{
                                borderBottom: "1px solid #f0f0f0",
                                cursor: "pointer",
                                ...(ticket.status === "open" ? { backgroundColor: "#fff3e0", borderLeft: "4px solid #ff9800", borderRadius: 0 } : {}),
                              }}
                              onClick={() => { setSelectedOrderTicket(ticket); setShowOrderDetails(true); }}
                              onMouseEnter={(e) => e.currentTarget.classList.add("ticket-row-hover")}
                              onMouseLeave={(e) => e.currentTarget.classList.remove("ticket-row-hover")}
                            >
                              <td style={{ padding: "14px 12px", fontWeight: "600", color: "#333", whiteSpace: "nowrap" }}>
                                {ticket.status === "open" && (
                                  <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#ff9800", marginRight: "6px" }}></span>
                                )}
                                {ticket.ticketNumber}
                              </td>
                              <td style={{ padding: "14px 12px" }}>
                                <div style={{ fontWeight: "500", color: "#333", fontSize: "13px" }}>{ticket.subject.substring(0, 28)}</div>
                                <div style={{ fontSize: "11px", color: "#999" }}>Car: {ticket.carModelName}</div>
                              </td>
                              <td style={{ padding: "14px 12px" }}>
                                <div style={{ fontWeight: "500", fontSize: "13px", color: "#333" }}>{ticket.userName}</div>
                                <div style={{ fontSize: "11px", color: "#999" }}>{ticket.userEmail}</div>
                              </td>
                              <td style={{ padding: "14px 12px" }}>
                                <span style={{ fontSize: "12px", fontWeight: "600", color: "#555", fontFamily: "monospace" }}>{ticket.orderRefId}</span>
                              </td>
                              <td style={{ padding: "14px 12px", textAlign: "center" }}>
                                <span style={{ backgroundColor: "#e3f2fd", color: "#1565c0", padding: "5px 10px", borderRadius: "4px", fontSize: "11px", fontWeight: "600", whiteSpace: "nowrap" }}>{ticket.categoryDisplay}</span>
                              </td>
                              <td style={{ padding: "14px 12px", textAlign: "center" }}>
                                <span style={{ backgroundColor: getStatusColor(ticket.status), color: "white", padding: "5px 11px", borderRadius: "4px", fontSize: "11px", fontWeight: "600" }}>{ticket.statusDisplay}</span>
                                {ticket.status === "closed" && ticket.ticketClosedBy && renderActorBadge(ticket.ticketClosedBy, ticket.ticketClosedByRole, "#d32f2f")}
                                {ticket.status === "re-open" && ticket.reopenedBy && renderActorBadge(ticket.reopenedBy, ticket.reopenedByRole, "#9c27b0")}
                              </td>
                              <td style={{ padding: "14px 12px", textAlign: "center" }}>
                                <span style={{ backgroundColor: getPriorityColor(ticket.priority), color: "white", padding: "5px 11px", borderRadius: "4px", fontSize: "11px", fontWeight: "600" }}>{ticket.priorityDisplay}</span>
                              </td>
                              <td style={{ padding: "14px 12px", textAlign: "center", color: "#333", whiteSpace: "nowrap" }}>📅 {formatDate(ticket.date)}</td>
                              <td style={{ padding: "14px 12px", textAlign: "center", fontSize: "12px", color: ticket.assignedTo && ticket.assignedTo !== "Unassigned" ? "#333" : "#999" }}>{ticket.assignedTo || "Unassigned"}</td>
                              <td style={{ padding: "14px 12px", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                <button onClick={(e) => { e.stopPropagation(); setSelectedOrderTicket(ticket); setShowOrderDetails(true); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#1976d2", fontSize: "16px", padding: "4px 8px" }} title="View Details">👁️</button>
                                <div style={{ display: "inline-block", position: "relative" }}>
                                  <button onClick={(e) => { e.stopPropagation(); handleOpenOrderChat(ticket); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#d32f2f", fontSize: "16px", padding: "4px 8px" }} title="Chat with User">💬</button>
                                  {orderUnreadCounts[ticket.id] > 0 && (
                                    <span style={{ position: "absolute", top: "-8px", right: "-8px", backgroundColor: "#d32f2f", color: "white", borderRadius: "50%", width: "20px", height: "20px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "bold" }}>{orderUnreadCounts[ticket.id]}</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {renderPagination(orderCurrentPage, setOrderCurrentPage, orderTotalPages, orderItemsPerPage, setOrderItemsPerPage)}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* -- Order Ticket Details Modal ------------------------------------------- */}
      {showOrderDetails && selectedOrderTicket && (
        <div
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={() => { setShowOrderDetails(false); setSelectedOrderTicket(null); }}
        >
          <div
            style={{ backgroundColor: "white", borderRadius: "6px", width: "90%", maxWidth: "700px", maxHeight: "90vh", overflow: "auto", boxShadow: "0 8px 24px rgba(0,0,0,0.15)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "24px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, color: "#d32f2f", fontSize: "18px", fontWeight: "600" }}>
                {selectedOrderTicket.ticketNumber}
              </h2>
              <button onClick={() => { setShowOrderDetails(false); setSelectedOrderTicket(null); }} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#999" }}>×</button>
            </div>

            <div style={{ padding: "24px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
                {[
                  { label: "SUBJECT", value: selectedOrderTicket.subject },
                  { label: "ORDER REF", value: selectedOrderTicket.orderRefId },
                  { label: "CUSTOMER", value: selectedOrderTicket.userName, sub: selectedOrderTicket.userEmail },
                  { label: "CAR MODEL", value: selectedOrderTicket.carModelName },
                  { label: "MOBILE", value: `${selectedOrderTicket.countryCode} ${selectedOrderTicket.alternativeMobileNumber}` },
                  { label: "TICKET CREATION DATE", value: formatDate(convertDateFormat(selectedOrderTicket.creationDate) || selectedOrderTicket.date) },
                ].map(({ label, value, sub }) => (
                  <div key={label}>
                    <p style={{ margin: "0 0 6px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>{label}</p>
                    <p style={{ margin: 0, fontSize: "14px", fontWeight: "500", color: "#333" }}>{value}</p>
                    {sub && <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "#666" }}>{sub}</p>}
                  </div>
                ))}
              </div>

              <hr style={{ margin: "20px 0", border: "none", borderTop: "1px solid #f0f0f0" }} />

              <p style={{ margin: "0 0 6px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>DESCRIPTION</p>
              <p style={{ margin: "0 0 20px 0", fontSize: "14px", color: "#333", lineHeight: "1.6", backgroundColor: "#fafafa", padding: "12px", borderRadius: "4px", border: "1px solid #f0f0f0" }}>
                {selectedOrderTicket.description}
              </p>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "15px", marginBottom: "20px" }}>
                <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "4px", textAlign: "center" }}>
                  <p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>STATUS</p>
                  <span style={{ backgroundColor: getStatusColor(selectedOrderTicket.status), color: "white", padding: "6px 12px", borderRadius: "4px", fontSize: "12px", fontWeight: "600" }}>{selectedOrderTicket.statusDisplay}</span>
                </div>
                <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "4px", textAlign: "center" }}>
                  <p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>PRIORITY</p>
                  <span style={{ backgroundColor: getPriorityColor(selectedOrderTicket.priority), color: "white", padding: "6px 12px", borderRadius: "4px", fontSize: "12px", fontWeight: "600" }}>{selectedOrderTicket.priorityDisplay}</span>
                </div>
                <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "4px", textAlign: "center" }}>
                  <p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>CATEGORY</p>
                  <span style={{ backgroundColor: "#e3f2fd", color: "#1565c0", padding: "6px 12px", borderRadius: "4px", fontSize: "12px", fontWeight: "600" }}>{selectedOrderTicket.categoryDisplay}</span>
                </div>
              </div>

              {(selectedOrderTicket.ticketClosedAt || selectedOrderTicket.reopenCount > 0) && (
                <div style={{ backgroundColor: "#fff8e1", padding: "15px", borderRadius: "6px", border: "1px solid #ffe082" }}>
                  <p style={{ margin: "0 0 8px 0", fontSize: "12px", fontWeight: "700", color: "#f57f17" }}>TICKET ACTIVITY</p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", fontSize: "13px", color: "#555" }}>
                    {selectedOrderTicket.ticketClosedAt && (
                      <>
                        <span style={{ fontWeight: "600" }}>Closed By:</span>
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          {isUserRole(selectedOrderTicket.ticketClosedByRole) ? <PersonIcon style={{ fontSize: "14px", color: "#d32f2f" }} /> : <SupportAgentIcon style={{ fontSize: "16px", color: "#d32f2f" }} />}
                          {selectedOrderTicket.ticketClosedBy || "Admin"}
                        </span>
                        <span style={{ fontWeight: "600" }}>Closed At:</span>
                        <span>{selectedOrderTicket.ticketClosedAt}</span>
                      </>
                    )}
                    {selectedOrderTicket.reopenedBy && (
                      <>
                        <span style={{ fontWeight: "600" }}>Reopened By:</span>
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          {isUserRole(selectedOrderTicket.reopenedByRole) ? <PersonIcon style={{ fontSize: "14px", color: "#9c27b0" }} /> : <SupportAgentIcon style={{ fontSize: "16px", color: "#9c27b0" }} />}
                          {selectedOrderTicket.reopenedBy}
                        </span>
                        {selectedOrderTicket.ticketReopenAt && (
                          <>
                            <span style={{ fontWeight: "600" }}>Reopened At:</span>
                            <span>{selectedOrderTicket.ticketReopenAt}</span>
                          </>
                        )}
                      </>
                    )}
                    <span style={{ fontWeight: "600" }}>Reopen Count:</span>
                    <span>{selectedOrderTicket.reopenCount}</span>
                    <span style={{ fontWeight: "600" }}>Last Activity:</span>
                    <span>{selectedOrderTicket.lastActivityAt}</span>
                  </div>
                </div>
              )}

              {selectedOrderTicket.attachmentFileName && (
                <div style={{ marginTop: "16px", padding: "12px", backgroundColor: "#e8f5e9", borderRadius: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <span>📎</span>
                  <span style={{ fontSize: "13px", color: "#2e7d32", fontWeight: "500" }}>{selectedOrderTicket.attachmentFileName}</span>
                </div>
              )}
            </div>

            <div style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              {selectedOrderTicket.status === "closed" && (
                <button
                  onClick={() => reopenOrderTicket(selectedOrderTicket.ticketNumber)}
                  disabled={reopeningOrderTicket}
                  style={{ padding: "8px 16px", backgroundColor: reopeningOrderTicket ? "#ccc" : "#2e7d32", color: "white", border: "none", borderRadius: "4px", cursor: reopeningOrderTicket ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: "600", opacity: reopeningOrderTicket ? 0.7 : 1 }}
                >
                  {reopeningOrderTicket ? "Reopening..." : "Reopen Ticket"}
                </button>
              )}
              {selectedOrderTicket.status !== "closed" && (
                <button
                  onClick={() => closeOrderTicket(selectedOrderTicket.ticketNumber)}
                  disabled={closingOrderTicket}
                  style={{ padding: "8px 16px", backgroundColor: closingOrderTicket ? "#ccc" : "#d32f2f", color: "white", border: "none", borderRadius: "4px", cursor: closingOrderTicket ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: "600", opacity: closingOrderTicket ? 0.7 : 1 }}
                >
                  {closingOrderTicket ? "Closing..." : "Close Ticket"}
                </button>
              )}
              <button onClick={() => { setShowOrderDetails(false); setSelectedOrderTicket(null); }} style={{ padding: "8px 16px", backgroundColor: "transparent", color: "#666", border: "1px solid #ddd", borderRadius: "4px", cursor: "pointer", fontSize: "13px" }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* -- Order Ticket Chat Modal --------------------------------------------- */}
      {showOrderReplyChat && selectedOrderTicket && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={handleCloseOrderChat}>
          <div style={{ backgroundColor: "white", borderRadius: "6px", width: "90%", maxWidth: "700px", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 24px rgba(0,0,0,0.15)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ margin: "0 0 4px 0", color: "#d32f2f", fontSize: "16px", fontWeight: "600" }}>{selectedOrderTicket.ticketNumber}</h3>
                <p style={{ margin: 0, fontSize: "12px", color: "#999" }}>{selectedOrderTicket.subject}</p>
              </div>
              <button onClick={handleCloseOrderChat} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#999" }}>×</button>
            </div>
            {orderChatLoading && (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#fafafa" }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ display: "inline-block", width: "40px", height: "40px", border: "4px solid #f0f0f0", borderTop: "4px solid #d32f2f", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
                  <p style={{ marginTop: "12px", color: "#666", fontSize: "13px" }}>Loading chat...</p>
                </div>
              </div>
            )}
            {orderChatError && (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#fafafa" }}>
                <p style={{ color: "#d32f2f", fontSize: "13px" }}>!️ {orderChatError}</p>
              </div>
            )}
            {!orderChatLoading && !orderChatError && (
              <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", backgroundColor: "#fafafa" }}>
                {orderChatMessages.length === 0 && (
                  <div style={{ textAlign: "center", color: "#999", padding: "20px", fontSize: "13px" }}>No messages yet. Start a conversation!</div>
                )}
                {orderChatMessages.map((msg, idx) => (
                  <div key={idx} style={{ marginBottom: "16px", display: "flex", justifyContent: msg.isOwn ? "flex-end" : "flex-start", alignItems: "flex-end", gap: "8px" }}>
                    {!msg.isOwn && (
                      <div style={{ width: "30px", height: "30px", borderRadius: "50%", overflow: "hidden", flexShrink: 0, backgroundColor: "#fde8e8", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #f5c6c6" }}>
                        {userProfilePic ? <img src={userProfilePic} alt="user" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <PersonIcon style={{ fontSize: "18px", color: "#d32f2f" }} />}
                      </div>
                    )}
                    <div style={{ maxWidth: "70%", backgroundColor: msg.isOwn ? "#d32f2f" : "#fff", color: msg.isOwn ? "white" : "#333", padding: "12px 16px", borderRadius: "8px", border: msg.isOwn ? "none" : "1px solid #f0f0f0" }}>
                      <p style={{ margin: "0 0 6px 0", fontSize: "12px", fontWeight: "600" }}>{msg.isOwn ? `${msg.senderName} (Support)` : msg.senderName}</p>
                      {msg.message && <p style={{ margin: "0 0 6px 0", fontSize: "13px", lineHeight: "1.4" }}>{msg.message}</p>}
                      {msg.attachmentFileName && (
                        <div style={{ marginTop: "8px", padding: "8px", borderTop: msg.isOwn ? "1px solid rgba(255,255,255,0.2)" : "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: "8px", fontSize: "12px" }}>
                          <span>📎</span>
                          <button onClick={() => handleDownloadOrderAttachment(msg.id, msg.attachmentFileName)} disabled={orderDownloadingMessageId === msg.id} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: msg.isOwn ? "white" : "#1976d2", textDecoration: "underline", fontSize: "12px", fontWeight: "500", opacity: orderDownloadingMessageId === msg.id ? 0.7 : 1 }}>
                            {orderDownloadingMessageId === msg.id ? "Downloading..." : msg.attachmentFileName}
                          </button>
                        </div>
                      )}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "11px", opacity: 0.7, marginTop: "6px" }}>
                        <span>{formatChatTime(msg.timestamp)}</span>
                        {msg.isOwn && <MessageTick status={msg.status} />}
                      </div>
                    </div>
                    {msg.isOwn && (
                      <div style={{ width: "30px", height: "30px", borderRadius: "50%", backgroundColor: "#d32f2f", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <SupportAgentIcon style={{ fontSize: "16px", color: "white" }} />
                      </div>
                    )}
                  </div>
                ))}
                <div ref={orderChatBottomRef} />
              </div>
            )}
            {!orderChatLoading && !orderChatError && (
              <div style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", flexDirection: "column", gap: "10px", backgroundColor: "#fff" }}>
                {orderAttachedFile && (
                  <div style={{ fontSize: "12px", color: "#666", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 8px", backgroundColor: "#e3f2fd", borderRadius: "4px" }}>
                    <span>📎 Attached: <strong>{orderAttachedFile.name}</strong></span>
                    <button onClick={() => { setOrderAttachedFile(null); if (orderFileInputRef.current) orderFileInputRef.current.value = ""; }} style={{ background: "none", border: "none", color: "#d32f2f", cursor: "pointer", fontWeight: "bold", fontSize: "14px", padding: "0 4px" }}>×</button>
                  </div>
                )}
                <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
                  <textarea value={orderReplyMessage} onChange={(e) => setOrderReplyMessage(e.target.value)} placeholder={orderAttachedFile ? "Add a caption..." : "Type your reply..."} style={{ flex: 1, padding: "10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", minHeight: "60px", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
                  <label htmlFor="order-file-upload" style={{ padding: "8px 12px", borderRadius: "4px", cursor: "pointer", height: "fit-content", display: "flex", alignItems: "center" }} title={orderAttachedFile ? "Change File" : "Attach File"}>
                    <AttachFileIcon sx={{ fontSize: "20px" }} />
                    <input id="order-file-upload" type="file" ref={orderFileInputRef} onChange={handleOrderFileChange} style={{ display: "none" }} disabled={orderSendingMessage} />
                  </label>
                  <button onClick={handleSendOrderReply} disabled={(!orderReplyMessage.trim() && !orderAttachedFile) || orderSendingMessage} style={{ padding: "10px 16px", backgroundColor: (!orderReplyMessage.trim() && !orderAttachedFile) || orderSendingMessage ? "#ccc" : "#d32f2f", color: "white", border: "none", borderRadius: "4px", cursor: (!orderReplyMessage.trim() && !orderAttachedFile) || orderSendingMessage ? "not-allowed" : "pointer", fontWeight: "600", fontSize: "13px", whiteSpace: "nowrap", height: "fit-content", opacity: orderSendingMessage ? 0.7 : 1 }}>
                    {orderSendingMessage ? "Sending..." : "Send"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* -- Inquiry Details Modal ----------------------------------------------- */}
      {showDetails && selectedTicket && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={() => { setShowDetails(false); setSelectedTicket(null); }}>
          <div style={{ backgroundColor: "white", borderRadius: "6px", width: "90%", maxWidth: "700px", maxHeight: "90vh", overflow: "auto", boxShadow: "0 8px 24px rgba(0,0,0,0.15)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "24px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, color: "#d32f2f", fontSize: "18px", fontWeight: "600" }}>Ticket {selectedTicket.ticketNumber}</h2>
              <button onClick={() => { setShowDetails(false); setSelectedTicket(null); }} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#999" }}>×</button>
            </div>
            <div style={{ padding: "24px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "24px" }}>
                <div><p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>SUBJECT</p><p style={{ margin: 0, fontSize: "14px", fontWeight: "500", color: "#333" }}>{selectedTicket.subject}</p></div>
                <div><p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>TICKET CREATION DATE</p><p style={{ margin: 0, fontSize: "14px", fontWeight: "500", color: "#333" }}>📅 {formatDate(convertDateFormat(selectedTicket.creationDate) || selectedTicket.date)}</p></div>
                <div><p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>CUSTOMER</p><p style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: "500", color: "#333" }}>{selectedTicket.userName}</p><p style={{ margin: 0, fontSize: "12px", color: "#666" }}>{selectedTicket.userEmail}</p></div>
                <div><p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>CAR MODEL</p><p style={{ margin: 0, fontSize: "14px", fontWeight: "500", color: "#333" }}>{selectedTicket.carModel}</p></div>
                {selectedTicket.alternativeMobileNumber && (
                  <div><p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>CONTACT NUMBER</p><p style={{ margin: 0, fontSize: "14px", fontWeight: "500", color: "#333" }}>{selectedTicket.countryCode} {selectedTicket.alternativeMobileNumber}</p></div>
                )}
                {canManageTicketAssignment && (
                  <div>
                    <p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>ASSIGNED TO</p>
                    <select
                      value={ticketAssignedAdminSelection}
                      onChange={handleTicketAssignedAdminChange}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#333',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="">Unassigned</option>
                      {DUMMY_ADMIN_LIST_TICKETS.map((admin) => (
                        <option key={admin.id} value={admin.id}>
                          {admin.name} ({admin.role.replace('_', ' ')})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <hr style={{ margin: "24px 0", border: "none", borderTop: "1px solid #f0f0f0" }} />
              <p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>DESCRIPTION</p>
              <p style={{ margin: "0 0 24px 0", fontSize: "14px", color: "#333", lineHeight: "1.6", backgroundColor: "#fafafa", padding: "12px", borderRadius: "4px", border: "1px solid #f0f0f0" }}>{selectedTicket.description}</p>
              
              {/* Ticket Activity Section for Inquiry Tickets */}
              {(selectedTicket.ticketClosedAt || selectedTicket.reopenCount > 0) && (
                <div style={{ backgroundColor: "#fff8e1", padding: "15px", borderRadius: "6px", border: "1px solid #ffe082", marginBottom: "20px" }}>
                  <p style={{ margin: "0 0 8px 0", fontSize: "12px", fontWeight: "700", color: "#f57f17" }}>TICKET ACTIVITY</p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", fontSize: "13px", color: "#555" }}>
                    {selectedTicket.ticketClosedAt && (
                      <>
                        <span style={{ fontWeight: "600" }}>Closed By:</span>
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          {isUserRole(selectedTicket.ticketClosedByRole) ? <PersonIcon style={{ fontSize: "14px", color: "#d32f2f" }} /> : <SupportAgentIcon style={{ fontSize: "16px", color: "#d32f2f" }} />}
                          {selectedTicket.ticketClosedBy || "Admin"}
                        </span>
                        <span style={{ fontWeight: "600" }}>Closed At:</span>
                        <span>{selectedTicket.ticketClosedAt}</span>
                      </>
                    )}
                    {selectedTicket.reopenedBy && (
                      <>
                        <span style={{ fontWeight: "600" }}>Reopened By:</span>
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          {isUserRole(selectedTicket.reopenedByRole) ? <PersonIcon style={{ fontSize: "14px", color: "#9c27b0" }} /> : <SupportAgentIcon style={{ fontSize: "16px", color: "#9c27b0" }} />}
                          {selectedTicket.reopenedBy}
                        </span>
                        {selectedTicket.ticketReopenAt && (
                          <>
                            <span style={{ fontWeight: "600" }}>Reopened At:</span>
                            <span>{selectedTicket.ticketReopenAt}</span>
                          </>
                        )}
                      </>
                    )}
                    <span style={{ fontWeight: "600" }}>Reopen Count:</span>
                    <span>{selectedTicket.reopenCount || 0}</span>
                    <span style={{ fontWeight: "600" }}>Last Activity:</span>
                    <span>{selectedTicket.lastActivityAt || selectedTicket.date}</span>
                  </div>
                </div>
              )}
              
              {/*
              <div style={{ backgroundColor: "#f9f9f9", padding: "20px", borderRadius: "6px", marginBottom: "20px" }}>
                <h3 style={{ margin: "0 0 16px 0", color: "#d32f2f", fontSize: "14px", fontWeight: "600" }}>UPDATE TICKET</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                  <div>
                    <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: "600", color: "#333" }}>Status</label>
                    <select value={updatedStatus} onChange={(e) => setUpdatedStatus(e.target.value)} style={selectStyle}>
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="re-open">Re-Open</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: "600", color: "#333" }}>Assign To</label>
                    <select value={updatedAssignee} onChange={(e) => setUpdatedAssignee(e.target.value)} style={selectStyle}>
                      {agents.map((agent) => <option key={agent} value={agent}>{agent}</option>)}
                    </select>
                  </div>
                </div>
                <button onClick={() => setShowUpdate(true)} style={{ marginTop: "15px", padding: "10px 20px", backgroundColor: "#d32f2f", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>Save Changes</button>
              </div>
              */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "15px" }}>
                <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "4px", textAlign: "center" }}><p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>STATUS</p><span style={{ backgroundColor: getStatusColor(selectedTicket.status), color: "white", padding: "6px 12px", borderRadius: "4px", fontSize: "12px", fontWeight: "600" }}>{selectedTicket.status}</span></div>
                <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "4px", textAlign: "center" }}><p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>PRIORITY</p><span style={{ backgroundColor: getPriorityColor(selectedTicket.priority), color: "white", padding: "6px 12px", borderRadius: "4px", fontSize: "12px", fontWeight: "600" }}>{selectedTicket.priority}</span></div>
                <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "4px", textAlign: "center" }}><p style={{ margin: "0 0 8px 0", fontSize: "11px", color: "#999", fontWeight: "600" }}>TYPE</p><span style={{ backgroundColor: "#e3f2fd", color: "#1976d2", padding: "6px 12px", borderRadius: "4px", fontSize: "12px", fontWeight: "600" }}>{getTypeText(selectedTicket.type)}</span></div>
              </div>
            </div>
            <div style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              {selectedTicket.status === "closed" && (
                <button
                  onClick={() => reopenInquiryTicket(selectedTicket.ticketNumber)}
                  disabled={reopeningInquiryTicket}
                  style={{ padding: "8px 16px", backgroundColor: reopeningInquiryTicket ? "#ccc" : "#2e7d32", color: "white", border: "none", borderRadius: "4px", cursor: reopeningInquiryTicket ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: "600", opacity: reopeningInquiryTicket ? 0.7 : 1 }}
                >
                  {reopeningInquiryTicket ? "Reopening..." : "Reopen Ticket"}
                </button>
              )}
              {selectedTicket.status !== "closed" && (
                <button
                  onClick={() => closeInquiryTicket(selectedTicket.ticketNumber)}
                  disabled={closingInquiryTicket}
                  style={{ padding: "8px 16px", backgroundColor: closingInquiryTicket ? "#ccc" : "#d32f2f", color: "white", border: "none", borderRadius: "4px", cursor: closingInquiryTicket ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: "600", opacity: closingInquiryTicket ? 0.7 : 1 }}
                >
                  {closingInquiryTicket ? "Closing..." : "Close Ticket"}
                </button>
              )}
              <button 
                onClick={() => { setShowDetails(false); setSelectedTicket(null); }} 
                style={{ padding: "8px 16px", backgroundColor: "transparent", color: "#666", border: "1px solid #ddd", borderRadius: "4px", cursor: "pointer", fontSize: "13px" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -- Chat Modal ---------------------------------------------------------- */}
      {showReplyChat && selectedTicket && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={handleCloseInquiryChat}>
          <div style={{ backgroundColor: "white", borderRadius: "6px", width: "90%", maxWidth: "700px", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 24px rgba(0,0,0,0.15)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ margin: "0 0 4px 0", color: "#d32f2f", fontSize: "16px", fontWeight: "600" }}>{selectedTicket.ticketNumber}</h3>
                <p style={{ margin: 0, fontSize: "12px", color: "#999" }}>{selectedTicket.subject}</p>
              </div>
              <button onClick={handleCloseInquiryChat} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#999" }}>×</button>
            </div>
            {chatLoading && <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#fafafa" }}><div style={{ textAlign: "center" }}><div style={{ display: "inline-block", width: "40px", height: "40px", border: "4px solid #f0f0f0", borderTop: "4px solid #d32f2f", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div><p style={{ marginTop: "12px", color: "#666", fontSize: "13px" }}>Loading chat...</p></div></div>}
            {chatError && <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#fafafa" }}><p style={{ color: "#d32f2f", fontSize: "13px" }}>!️ {chatError}</p></div>}
            {!chatLoading && !chatError && (
              <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", backgroundColor: "#fafafa" }}>
                {chatMessages[selectedTicket.id]?.map((msg, idx) => (
                  <div key={idx} style={{ marginBottom: "16px", display: "flex", justifyContent: msg.isOwn ? "flex-end" : "flex-start", alignItems: "flex-end", gap: "8px" }}>
                    {!msg.isOwn && (
                      <div style={{ width: "30px", height: "30px", borderRadius: "50%", overflow: "hidden", flexShrink: 0, backgroundColor: "#fde8e8", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #f5c6c6" }}>
                        {userProfilePic ? <img src={userProfilePic} alt="user" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <PersonIcon style={{ fontSize: "18px", color: "#d32f2f" }} />}
                      </div>
                    )}
                    <div style={{ maxWidth: "70%", backgroundColor: msg.isOwn ? "#d32f2f" : "#fff", color: msg.isOwn ? "white" : "#333", padding: "12px 16px", borderRadius: "8px", border: msg.isOwn ? "none" : "1px solid #f0f0f0" }}>
                      <p style={{ margin: "0 0 6px 0", fontSize: "12px", fontWeight: "600" }}>{msg.isOwn ? `${msg.senderName} (Support)` : msg.senderName}</p>
                      <p style={{ margin: "0 0 6px 0", fontSize: "13px", lineHeight: "1.4" }}>{msg.message}</p>
                      {msg.attachmentFileName && (
                        <div style={{ marginTop: "8px", padding: "8px", borderTop: msg.isOwn ? "1px solid rgba(255,255,255,0.2)" : "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: "8px", fontSize: "12px" }}>
                          <span style={{ color: msg.isOwn ? "white" : "#666" }}>📎</span>
                          <button onClick={() => handleDownloadAttachment(msg.id, msg.attachmentFileName)} disabled={downloadingMessageId === msg.id} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: msg.isOwn ? "white" : "#1976d2", textDecoration: "underline", fontSize: "12px", fontWeight: "500", opacity: downloadingMessageId === msg.id ? 0.7 : 1 }}>
                            {downloadingMessageId === msg.id ? "Downloading..." : msg.attachmentFileName}
                          </button>
                        </div>
                      )}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "11px", opacity: 0.7, marginTop: "6px" }}>
                        <span>{formatChatTime(msg.timestamp)}</span>
                        {msg.isOwn && <MessageTick status={msg.status} />}
                      </div>
                    </div>
                    {msg.isOwn && (
                      <div style={{ width: "30px", height: "30px", borderRadius: "50%", backgroundColor: "#d32f2f", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <SupportAgentIcon style={{ fontSize: "16px", color: "white" }} />
                      </div>
                    )}
                  </div>
                ))}
                <div ref={inquiryChatBottomRef} />
              </div>
            )}
            {!chatLoading && !chatError && (
              <div style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", flexDirection: "column", gap: "10px", backgroundColor: "#fff" }}>
                {attachedFile && (
                  <div style={{ fontSize: "12px", color: "#666", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 8px", backgroundColor: "#e3f2fd", borderRadius: "4px" }}>
                    <span>📎 Attached: <strong>{attachedFile.name}</strong></span>
                    <button onClick={() => setAttachedFile(null)} style={{ background: "none", border: "none", color: "#d32f2f", cursor: "pointer", fontWeight: "bold", fontSize: "14px", padding: "0 4px" }}>×</button>
                  </div>
                )}
                <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
                  <textarea value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} placeholder={attachedFile ? "Add a caption..." : "Type your reply..."} style={{ flex: 1, padding: "10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", minHeight: "60px", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
                  <label htmlFor="file-upload" style={{ padding: "8px 12px", borderRadius: "4px", cursor: "pointer", height: "fit-content", display: "flex", alignItems: "center" }} title={attachedFile ? "Change File" : "Attach File"}>
                    <AttachFileIcon sx={{ fontSize: "20px" }} />
                    <input id="file-upload" type="file" ref={inquiryFileInputRef} onChange={handleFileChange} style={{ display: "none" }} disabled={sendingMessage} />
                  </label>
                  <button onClick={handleSendReply} disabled={(!replyMessage.trim() && !attachedFile) || sendingMessage} style={{ padding: "10px 16px", backgroundColor: (!replyMessage.trim() && !attachedFile) || sendingMessage ? "#ccc" : "#d32f2f", color: "white", border: "none", borderRadius: "4px", cursor: (!replyMessage.trim() && !attachedFile) || sendingMessage ? "not-allowed" : "pointer", fontWeight: "600", fontSize: "13px", whiteSpace: "nowrap", height: "fit-content", opacity: sendingMessage ? 0.7 : 1 }}>
                    {sendingMessage ? "Sending..." : "Send"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* -- Confirm Update Modal ------------------------------------------------ */}
      {showUpdate && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1001 }} onClick={() => setShowUpdate(false)}>
          <div style={{ backgroundColor: "white", borderRadius: "6px", width: "90%", maxWidth: "400px", boxShadow: "0 8px 24px rgba(0,0,0,0.15)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "24px" }}>
              <h3 style={{ margin: "0 0 16px 0", color: "#d32f2f", fontSize: "16px", fontWeight: "600" }}>Confirm Changes</h3>
              <p style={{ margin: "0 0 12px 0", fontSize: "13px", color: "#666" }}>Are you sure you want to update this ticket?</p>
              <div style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "4px", marginBottom: "20px" }}>
                <p style={{ margin: "0 0 8px 0", fontSize: "12px", fontWeight: "600", color: "#333" }}>Status: <span style={{ color: "#d32f2f" }}>{updatedStatus}</span></p>
                <p style={{ margin: 0, fontSize: "12px", fontWeight: "600", color: "#333" }}>Assign To: <span style={{ color: "#d32f2f" }}>{updatedAssignee}</span></p>
              </div>
            </div>
            <div style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button onClick={() => setShowUpdate(false)} style={{ padding: "8px 16px", backgroundColor: "transparent", color: "#666", border: "1px solid #ddd", borderRadius: "4px", cursor: "pointer", fontSize: "13px" }}>Cancel</button>
              <button onClick={() => { handleUpdateTicket(); setShowUpdate(false); }} style={{ padding: "8px 16px", backgroundColor: "#d32f2f", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "13px" }}>Confirm Update</button>
            </div>
          </div>
        </div>
      )}

      {/* -- Success Toast ------------------------------------------------------- */}
      {showSuccess && (
        <div style={{ position: "fixed", bottom: "20px", right: "20px", backgroundColor: successMessage.includes("Failed") ? "#d32f2f" : "#4caf50", color: "white", padding: "14px 20px", borderRadius: "4px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 2000, fontSize: "13px", fontWeight: "500", animation: "slideIn 0.3s ease-out" }}>
          {successMessage.includes("Failed") ? "!️" : "v"} {successMessage}
        </div>
      )}

      <style>{`
        @keyframes slideIn { from { transform: translateX(400px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .ticket-row-active { background-color: #ffffff; }
        .ticket-row-read { background-color: #f5f5f5; opacity: 0.85; }
        .ticket-row-hover { background-color: #f0f0f0 !important; }
      `}</style>
    </div>
  );
};

export default AdminTickets;